import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FaArrowLeft, FaCreditCard, FaMedal, FaCalendarAlt, FaUser,
  FaCheckCircle, FaTimesCircle, FaEdit, FaSave, FaTimes,
  FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClipboardList,
  FaIdCard, FaKey, FaLock, FaShieldAlt, FaStickyNote, FaPlus,
  FaTrash, FaFileSignature, FaHistory, FaExclamationCircle,
  FaCheck, FaDownload, FaUpload, FaEye, FaSync,
  FaRegClock, FaRegCalendarAlt,
  FaUserPlus, FaGraduationCap, FaBaseballBall, FaLink, FaExternalLinkAlt,
  FaDollarSign,
} from "react-icons/fa";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import { useSort } from "./hooks/useSort";
import API_BASE from "./apiConfig";
import { useDateTimeFormat } from "./utils/formatters";
import { PrimaryButton, GhostButton } from "./components/ThemedButton";
import StatusBadge from "./components/StatusBadge";

const BASE   = API_BASE;
const FONT   = "'Segoe UI', system-ui, sans-serif";
const BLUR   = "blur(18px)";

// ── Helpers ──────────────────────────────────────────────────────────────────

function glassCard(T, extra = {}) {
  return {
    background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    border: T.glassBdr, borderRadius: 16, boxShadow: T.glassSh,
    padding: "20px 24px", boxSizing: "border-box", fontFamily: FONT, ...extra,
  };
}

function SectionHead({ title }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                  letterSpacing: "0.07em", color: "#6b7280", fontFamily: FONT, marginBottom: 14 }}>
      {title}
    </div>
  );
}

function Toast({ ok, text }) {
  if (!text) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
                  borderRadius: 10, marginBottom: 14, fontSize: 13, fontFamily: FONT,
                  background: ok ? "rgba(5,150,105,0.1)" : "rgba(220,38,38,0.1)",
                  color: ok ? getBrandColors().green : getBrandColors().red,
                  border: `1px solid ${ok ? "rgba(5,150,105,0.25)" : "rgba(220,38,38,0.25)"}` }}>
      {ok ? <FaCheckCircle size={13} /> : <FaExclamationCircle size={13} />} {text}
    </div>
  );
}

// Thin wrappers kept so every existing call site (many, throughout this file)
// stays unchanged; they now delegate to the real themed button components.
function Btn({ onClick, children, disabled, style = {} }) {
  return (
    <PrimaryButton onClick={onClick} disabled={disabled} style={{ fontFamily: FONT, ...style }}>
      {children}
    </PrimaryButton>
  );
}

function GhostBtn({ onClick, children, style = {} }) {
  return (
    <GhostButton onClick={onClick} style={{ fontFamily: FONT, ...style }}>
      {children}
    </GhostButton>
  );
}

const STATUS_CHIP = {
  confirmed:       { label: "Confirmed",       tone: "success" },
  pending_payment: { label: "Pending Payment", tone: "warning" },
  checked_in:      { label: "Checked In",      tone: "info" },
  completed:       { label: "Completed",        tone: "info" },
  cancelled:       { label: "Cancelled",        tone: "neutral" },
  no_show:         { label: "No Show",          tone: "danger" },
};

const CHARGE_STATUS_CHIP = {
  paid:    { label: "Paid",    tone: "success" },
  pending: { label: "Pending", tone: "warning" },
  failed:  { label: "Failed",  tone: "danger" },
};

const CLIENT_STATUS = {
  active:    { label: "Active",    tone: "success" },
  pending:   { label: "Pending",   tone: "warning" },
  suspended: { label: "Suspended", tone: "danger" },
  inactive:  { label: "Inactive",  tone: "neutral" },
};

const PURPOSE_LABEL = {
  lane_membership:    "Lane Membership",
  individual_booking: "Non-Member",
  team_membership:    "Team Membership",
  coaching_program:   "Coaching Program",
};

function Chip({ status, map }) {
  const m = map[status] || { label: status || "—", tone: "neutral" };
  return <StatusBadge label={m.label} tone={m.tone} style={{ whiteSpace: "nowrap" }} />;
}

function fmtMoney(v) { return `$${parseFloat(v || 0).toFixed(2)}`; }

function inputStyle(isDark) {
  return {
    padding: "9px 13px", borderRadius: 8,
    border: `1.5px solid ${isDark ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
    background: isDark ? "rgba(255,255,255,0.07)" : "#fff",
    color: isDark ? "#f1f5f9" : "#1e293b",
    fontSize: 13, fontFamily: FONT, outline: "none",
    width: "100%", boxSizing: "border-box",
  };
}

// ── TABS ─────────────────────────────────────────────────────────────────────

const TABS = [
  { key: "details",    label: "Details",    icon: <FaUser size={12} /> },
  { key: "membership", label: "Membership", icon: <FaMedal size={12} /> },
  { key: "login",      label: "Login",      icon: <FaKey size={12} /> },
  { key: "billing",    label: "Billing",    icon: <FaHistory size={12} /> },
  { key: "waiver",     label: "Waiver",     icon: <FaFileSignature size={12} /> },
  { key: "notes",      label: "Notes",      icon: <FaStickyNote size={12} /> },
  { key: "bookings",   label: "Bookings",   icon: <FaClipboardList size={12} /> },
  { key: "student",    label: "Student",    icon: <FaGraduationCap size={12} /> },
];

// ── 1. Details Tab — always editable, personal+address merged ─────────────────

function DetailsTab({ client, isDark, T, onSaved }) {
  const [draft,         setDraft]         = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [msg,           setMsg]           = useState(null);
  const [statusChanging, setStatusChanging] = useState(false);
  const [statusReason,  setStatusReason]  = useState("");

  useEffect(() => {
    setDraft({
      first_name: client.first_name || "", last_name: client.last_name || "",
      email: client.email || "",           phone: client.phone || "",
      date_of_birth: client.date_of_birth || "", gender: client.gender || "",
      street_address: client.street_address || "", city: client.city || "",
      state: client.state || "",           zip_code: client.zip_code || "",
      emergency_name: client.emergency_name || "",
      emergency_phone: client.emergency_phone || "",
      emergency_relation: client.emergency_relation || "",
    });
  }, [client]);

  const inp = inputStyle(isDark);

  const Field = (label, key, type = "text") => (
    <div key={key}>
      <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase",
                    letterSpacing: "0.06em", color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>
        {label}
      </div>
      <input type={type} value={draft?.[key] || ""}
        onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))} style={inp} />
    </div>
  );

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (r.ok) { setMsg({ ok: true, text: "Details saved." }); onSaved(); }
      else       { setMsg({ ok: false, text: "Save failed." }); }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    setSaving(false);
    setTimeout(() => setMsg(null), 4000);
  };

  const changeStatus = async (newStatus) => {
    const label = { active: "Activate", suspended: "Suspend", inactive: "Delete" }[newStatus];
    if (!window.confirm(`${label} this customer?`)) return;
    setStatusChanging(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}/set-status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, reason: statusReason || undefined }),
      });
      const d = await r.json();
      if (d.ok) { setMsg({ ok: true, text: `Status changed to ${newStatus}.` }); setStatusReason(""); onSaved(); }
      else       { setMsg({ ok: false, text: d.detail || "Failed." }); }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    setStatusChanging(false);
    setTimeout(() => setMsg(null), 4000);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Toast ok={msg?.ok} text={msg?.text} />

      {/* Personal info + Address — one card */}
      <div style={glassCard(T)}>
        <SectionHead title={<><FaUser size={10} style={{ marginRight: 6 }}/> Personal Information &amp; Address</>} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {Field("First Name", "first_name")}
          {Field("Last Name",  "last_name")}
          {Field("Email",      "email", "email")}
          {Field("Phone",      "phone", "tel")}
          {Field("Date of Birth", "date_of_birth", "date")}
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase",
                          letterSpacing: "0.06em", color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>
              Gender
            </div>
            <select value={draft?.gender || ""}
              onChange={e => setDraft(d => ({ ...d, gender: e.target.value }))} style={inp}>
              <option value="">—</option>
              <option>Male</option><option>Female</option>
              <option>Non-binary</option><option>Prefer not to say</option>
            </select>
          </div>
          <div style={{ gridColumn: "1/-1" }}>{Field("Street Address", "street_address")}</div>
          {Field("City",           "city")}
          {Field("State",          "state")}
          {Field("Zip / Postcode", "zip_code")}
        </div>
      </div>

      {/* Emergency contact */}
      <div style={glassCard(T)}>
        <SectionHead title={<><FaPhoneAlt size={10} style={{ marginRight: 6 }}/> Emergency Contact</>} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {Field("Name",         "emergency_name")}
          {Field("Phone",        "emergency_phone", "tel")}
          <div style={{ gridColumn: "1/-1" }}>{Field("Relationship", "emergency_relation")}</div>
        </div>
      </div>

      {/* Status Management */}
      {(() => {
        const cstMeta = CLIENT_STATUS[client.status] || { label: client.status, tone: "neutral" };
        const isActive    = client.status === "active";
        const isSuspended = client.status === "suspended";
        const isInactive  = client.status === "inactive";
        const isPending   = client.status === "pending";
        return (
          <div style={glassCard(T)}>
            <SectionHead title={<><FaShieldAlt size={10} style={{ marginRight: 6 }}/> Account Status</>} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.textLabel, fontFamily: FONT }}>Current Status:</span>
              <StatusBadge label={cstMeta.label} tone={cstMeta.tone}
                style={{ fontSize: 13, fontWeight: 800, padding: "4px 14px", fontFamily: FONT }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase",
                            letterSpacing: "0.06em", color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>
                Reason / Note (optional)
              </div>
              <input value={statusReason} onChange={e => setStatusReason(e.target.value)}
                placeholder="e.g. Account issue, manual activation…"
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                         background: isDark ? "rgba(255,255,255,0.07)" : "#f8fafc",
                         border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e2e8f0",
                         color: T.text, fontFamily: FONT, boxSizing: "border-box", outline: "none" }} />
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {!isActive && (
                <button onClick={() => changeStatus("active")} disabled={statusChanging}
                  style={{ padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer",
                           fontWeight: 700, fontSize: 13, fontFamily: FONT, ...T.statusPill("success") }}>
                  <FaCheckCircle size={11} style={{ marginRight: 6 }}/>Activate
                </button>
              )}
              {(isActive || isPending) && (
                <button onClick={() => changeStatus("suspended")} disabled={statusChanging}
                  style={{ padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer",
                           fontWeight: 700, fontSize: 13, fontFamily: FONT, ...T.statusPill("danger") }}>
                  <FaLock size={11} style={{ marginRight: 6 }}/>Suspend
                </button>
              )}
              {!isInactive && (
                <button onClick={() => changeStatus("inactive")} disabled={statusChanging}
                  style={{ padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer",
                           fontWeight: 700, fontSize: 13, fontFamily: FONT, ...T.statusPill("neutral") }}>
                  <FaTimesCircle size={11} style={{ marginRight: 6 }}/>Delete / Deactivate
                </button>
              )}
            </div>
          </div>
        );
      })()}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Btn T={T} onClick={save} disabled={saving}>
          <FaSave size={12}/> {saving ? "Saving…" : "Save Details"}
        </Btn>
      </div>
    </div>
  );
}

// ── 2. Membership Tab ─────────────────────────────────────────────────────────

function MembershipTab({ client, T, isDark, onSaved }) {
  const { fmtDate } = useDateTimeFormat();
  const mem = client.membership;
  const today = new Date();
  const isExpired = mem?.end_date && new Date(mem.end_date) < today;
  const daysLeft  = mem?.end_date ? Math.max(0, Math.ceil((new Date(mem.end_date) - today) / 86400000)) : null;

  const FREQ_LABEL = { once: "One-time", monthly: "Monthly", quarterly: "Quarterly", annually: "Annual" };

  const [plans,     setPlans]     = useState([]);
  const [showForm,  setShowForm]  = useState(false);
  const [selPlan,   setSelPlan]   = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [amtPaid,   setAmtPaid]   = useState("0");
  const [assigning, setAssigning] = useState(false);
  const [msg,       setMsg]       = useState(null);
  const [prorate,   setProrate]   = useState(null);
  const [newProrate, setNewProrate] = useState(null);

  const [history,     setHistory]     = useState([]);
  const [showExtras,  setShowExtras]  = useState(false);
  const [extraFees,   setExtraFees]   = useState([]);
  const [newFeeLabel, setNewFeeLabel] = useState("");
  const [newFeeAmt,   setNewFeeAmt]   = useState("");
  const [newFeeRecur, setNewFeeRecur] = useState(false);
  const [discAmt,     setDiscAmt]     = useState("");
  const [discType,    setDiscType]    = useState("amt");
  const [discLabel,   setDiscLabel]   = useState("");
  const [discRecur,   setDiscRecur]   = useState(false);
  const [savingExtras, setSavingExtras] = useState(false);

  const inp = inputStyle(isDark);

  const loadHistory = useCallback(() => {
    fetch(`${BASE}/lb/clients/${client.client_id}/memberships`)
      .then(r => r.json())
      .then(d => setHistory(d.memberships || []))
      .catch(() => setHistory([]));
  }, [client.client_id]);

  useEffect(() => {
    fetch(`${BASE}/lb/membership-plans`)
      .then(r => r.json())
      .then(d => setPlans(Array.isArray(d) ? d : (d.plans || [])))
      .catch(() => {});
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Pre-fill the extras editor from whatever's already on the current membership
  useEffect(() => {
    if (!mem) return;
    try { setExtraFees(mem.extra_fees_json ? JSON.parse(mem.extra_fees_json) : []); }
    catch { setExtraFees([]); }
    setDiscAmt(mem.discount_amount != null ? String(mem.discount_amount) : "");
    setDiscType(mem.discount_type || "amt");
    setDiscLabel(mem.discount_label || "");
    setDiscRecur(!!mem.discount_recurring);
  }, [mem?.membership_id]); // eslint-disable-line

  const addExtraFee = () => {
    if (!newFeeLabel.trim() || !newFeeAmt) return;
    setExtraFees(f => [...f, { id: Date.now(), label: newFeeLabel.trim(), amount: parseFloat(newFeeAmt), recurring: newFeeRecur }]);
    setNewFeeLabel(""); setNewFeeAmt(""); setNewFeeRecur(false);
  };
  const removeExtraFee = (id) => setExtraFees(f => f.filter(x => x.id !== id));

  const saveExtras = async () => {
    setSavingExtras(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}/membership/extras`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          extra_fees: extraFees,
          special_discount: { amount: parseFloat(discAmt) || 0, discType, label: discLabel, recurring: discRecur },
        }),
      });
      const d = await r.json();
      if (d.ok) {
        setMsg({ ok: true, text: `Saved. Recurring amount now $${parseFloat(d.recurring_amount).toFixed(2)}/cycle.` });
        setShowExtras(false); onSaved(); loadHistory();
      } else {
        setMsg({ ok: false, text: d.detail || "Failed to save." });
      }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    setSavingExtras(false);
    setTimeout(() => setMsg(null), 6000);
  };

  // Live preview of what the backend will ACTUALLY do on Confirm — a plan
  // change always takes effect today (routers/lane_booking.py::
  // _process_plan_change_billing), never the "Effective From" date, so this
  // must mirror that exact formula: credit is based on the OLD plan's own
  // monthly price and its own remaining paid-through days (never the
  // admin fee or any other one-time fee baked into amount_paid), and the
  // new plan is charged prorated for today through end of this month —
  // never the new plan's full price.
  useEffect(() => {
    if (!selPlan || !mem) { setProrate(null); return; }
    const newPlan = plans.find(p => String(p.plan_id) === String(selPlan));
    if (!newPlan) { setProrate(null); return; }

    const now    = new Date();
    const memEnd = new Date(mem.end_date + "T00:00:00");

    const unusedDays = Math.max(0, Math.round((memEnd - now) / 86400000) + 1);
    const daysInOldMonth = new Date(memEnd.getFullYear(), memEnd.getMonth() + 1, 0).getDate();
    const oldPlanPrice = parseFloat(mem.price || 0);
    const credit = Math.round((oldPlanPrice / daysInOldMonth) * unusedDays * 100) / 100;

    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const daysLeft = daysInMonth - now.getDate() + 1;
    const newPlanPrice = parseFloat(newPlan.price || 0);
    const newCharge = Math.round((newPlanPrice / daysInMonth) * daysLeft * 100) / 100;

    setProrate({ credit, newCharge, unusedDays, daysLeft, daysInMonth,
                 net: +(newCharge - credit).toFixed(2),
                 effectiveFrom: now.toISOString().split("T")[0] });
  }, [selPlan, mem, plans]); // eslint-disable-line

  // Live preview for a FIRST-TIME assignment (no existing membership to
  // prorate a credit against) — mirrors the server-side formula in
  // routers/lane_booking.py::assign_membership's non-plan-change branch:
  // today through end of the current calendar month.
  useEffect(() => {
    if (!selPlan || mem) { setNewProrate(null); return; }
    const newPlan = plans.find(p => String(p.plan_id) === String(selPlan));
    if (!newPlan) { setNewProrate(null); return; }
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const prorateDays = daysInMonth - now.getDate() + 1;
    const planPrice = parseFloat(newPlan.price || 0);
    const prorated = Math.round((planPrice / daysInMonth) * prorateDays * 100) / 100;
    setNewProrate({ prorated, prorateDays, daysInMonth, planPrice });
  }, [selPlan, mem, plans]);

  const assignPlan = async () => {
    if (!selPlan) { setMsg({ ok: false, text: "Select a membership plan." }); return; }
    setAssigning(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}/membership`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: parseInt(selPlan),
          start_date: startDate,
          amount_paid: prorate ? Math.max(0, prorate.newCharge) : (newProrate ? newProrate.prorated : parseFloat(amtPaid) || 0),
        }),
      });
      const d = await r.json();
      if (d.ok || d.membership_id) {
        const chargedMsg = d.total_charged != null ? ` Charged $${parseFloat(d.total_charged).toFixed(2)}.` : "";
        setMsg({ ok: true, text: `Membership assigned. Expires ${d.end_date || "—"}.${chargedMsg}` });
        setShowForm(false); setProrate(null); setNewProrate(null); onSaved(); loadHistory();
      } else {
        setMsg({ ok: false, text: d.detail || "Failed to assign." });
      }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    setAssigning(false);
    setTimeout(() => setMsg(null), 5000);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Toast ok={msg?.ok} text={msg?.text} />

      <div style={glassCard(T)}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <SectionHead title={<><FaMedal size={11} style={{ marginRight: 6 }}/> Current Membership</>} />
          <Btn T={T} onClick={() => setShowForm(s => !s)} style={{ padding: "7px 14px", fontSize: 12 }}>
            {showForm ? <><FaTimes size={10}/> Cancel</> : <><FaPlus size={10}/> {mem ? "Change Plan" : "Assign Plan"}</>}
          </Btn>
        </div>

        {showForm && (
          <div style={{ background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
                        borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 140px", gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
                              color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>Plan</div>
                <select value={selPlan} onChange={e => setSelPlan(e.target.value)} style={inp}>
                  <option value="">— select —</option>
                  {plans.map(p => (
                    <option key={p.plan_id} value={p.plan_id}>
                      {p.plan_name} ({p.duration_months}mo · ${p.price})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
                              color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>Effective From</div>
                {mem ? (
                  <>
                    <input type="date" value={new Date().toISOString().split("T")[0]} disabled
                      style={{ ...inp, opacity: 0.6, cursor: "not-allowed" }} />
                    <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3, fontFamily: FONT }}>
                      Plan changes always take effect immediately: the old plan's unused days are
                      credited (admin/other one-time fees excluded), the new plan is charged prorated
                      through end of this month, and the existing recurring schedule bills the new
                      price starting its normal next cycle.
                    </div>
                  </>
                ) : (
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inp} />
                )}
              </div>
            </div>

            {/* First-time assignment: server computes and charges the prorated
                amount automatically (today through end of month, plus any
                additional fees/discount on the plan) — no manual amount entry. */}
            {!mem && newProrate && (
              <div style={{ marginBottom: 14, padding: "12px 16px", borderRadius: 10,
                            background: "rgba(16,185,129,0.08)",
                            border: "1px solid rgba(16,185,129,0.25)" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#10b981",
                              fontFamily: FONT, marginBottom: 8 }}>
                  Prorated First Charge (today through end of month)
                </div>
                <div style={{ display: "flex", gap: 16, alignItems: "baseline" }}>
                  <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
                    {newProrate.prorateDays}/{newProrate.daysInMonth} days × ${newProrate.planPrice.toFixed(2)}/mo
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#10b981", fontFamily: FONT }}>
                    {fmtMoney(newProrate.prorated)}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 6 }}>
                  Full price then bills automatically starting next month. Add any additional fees or a
                  discount from "Manage Extras" after assigning — the recurring amount updates immediately.
                </div>
              </div>
            )}

            {/* Prorate calculation display */}
            {prorate && (
              <div style={{ marginBottom: 14, padding: "12px 16px", borderRadius: 10,
                            background: "rgba(99,102,241,0.08)",
                            border: "1px solid rgba(99,102,241,0.25)" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#6366f1",
                              fontFamily: FONT, marginBottom: 8 }}>
                  Prorated Calculation (effective today, {fmtDate(prorate.effectiveFrom)})
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#10b981", fontFamily: FONT, marginBottom: 3 }}>
                      Refund — Unused {mem.plan_name} ({prorate.unusedDays}d)
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#10b981", fontFamily: FONT }}>{fmtMoney(prorate.credit)}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6366f1", fontFamily: FONT, marginBottom: 3 }}>
                      New Charge — {plans.find(p => String(p.plan_id) === String(selPlan))?.plan_name} ({prorate.daysLeft}/{prorate.daysInMonth}d)
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#6366f1", fontFamily: FONT }}>{fmtMoney(prorate.newCharge)}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6b7280", fontFamily: FONT, marginBottom: 3 }}>Net Effect</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#6b7280", fontFamily: FONT }}>{prorate.net >= 0 ? "+" : "-"}{fmtMoney(Math.abs(prorate.net))}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#6366f1", fontFamily: FONT, marginTop: 8 }}>
                  Two separate transactions: a {fmtMoney(prorate.credit)} refund on the {mem.plan_name} charge
                  (admin fee and any other one-time fees excluded), and a {fmtMoney(prorate.newCharge)} charge for
                  the new plan. Full price then bills automatically starting the existing recurring schedule's next cycle.
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <Btn T={T} onClick={assignPlan} disabled={assigning} style={{ padding: "9px 20px" }}>
                <FaCheck size={11}/> {assigning ? "Saving…" : "Confirm"}
              </Btn>
              <GhostBtn onClick={() => { setShowForm(false); setProrate(null); }} T={T}>Cancel</GhostBtn>
            </div>
          </div>
        )}

        {mem ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
              <div style={{ width: 48, height: 48, borderRadius: 13, flexShrink: 0,
                             background: "linear-gradient(135deg,rgba(99,102,241,0.25),rgba(139,92,246,0.2))",
                             display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FaMedal size={22} color="#6366f1"/>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#6366f1", fontFamily: FONT }}>
                  {mem.plan_name}
                </div>
                {mem.plan_description && (
                  <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
                    {mem.plan_description}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, borderRadius: 20, padding: "5px 14px",
                             background: isExpired ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)",
                             color: isExpired ? "#ef4444" : "#10b981",
                             border: `1px solid ${isExpired ? "rgba(239,68,68,0.3)" : "rgba(16,185,129,0.3)"}` }}>
                {isExpired ? "Expired" : "Active"}
              </span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 18 }}>
              {[
                { label: "Start Date",  value: fmtDate(mem.start_date),                      color: "#6366f1" },
                { label: "End Date",    value: fmtDate(mem.end_date),                         color: isExpired ? "#ef4444" : "#10b981" },
                { label: "Days Left",   value: isExpired ? "Expired" : `${daysLeft}d`,        color: isExpired ? "#ef4444" : "#f59e0b" },
                { label: "Plan Price",  value: mem.price != null ? fmtMoney(mem.price) : "—", color: "#0ea5e9" },
                { label: "Amount Paid", value: fmtMoney(mem.amount_paid),                     color: "#10b981" },
                { label: "Billing",     value: FREQ_LABEL[mem.billing_frequency] || "—",      color: "#7c3aed" },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: `${color}10`, border: `1px solid ${color}25`,
                                          borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color, textTransform: "uppercase",
                                letterSpacing: "0.05em", fontFamily: FONT, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color, fontFamily: FONT }}>{value}</div>
                </div>
              ))}
            </div>

            {mem.max_weekly_hours != null && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", marginBottom: 14,
                            borderRadius: 10, background: "rgba(99,102,241,0.06)",
                            border: "1px solid rgba(99,102,241,0.18)" }}>
                <FaCalendarAlt size={14} color="#6366f1"/>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#6366f1", fontFamily: FONT }}>
                    Weekly Booking Allowance
                  </div>
                  <div style={{ fontSize: 13, color: T.text, fontFamily: FONT, marginTop: 2 }}>
                    <strong>{mem.max_weekly_hours} hours</strong> per week included
                  </div>
                </div>
              </div>
            )}

            {/* ── Additional Fees & Special Discount — placeholder to store
                  and display what was applied to THIS membership, editable
                  after the fact without redoing the whole plan assignment ── */}
            <div style={{ border: `1px solid ${T.divider}`, borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showExtras ? 12 : 0 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: T.text, fontFamily: FONT }}>Additional Fees &amp; Special Discount</div>
                  {!showExtras && !(extraFees.length || mem.discount_amount) && (
                    <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>None applied to this membership.</div>
                  )}
                </div>
                <GhostBtn onClick={() => setShowExtras(v => !v)} T={T} style={{ padding: "6px 12px", fontSize: 11 }}>
                  {showExtras ? <><FaTimes size={9}/> Cancel</> : <><FaPlus size={9}/> {(extraFees.length || mem.discount_amount) ? "Edit" : "Add"}</>}
                </GhostBtn>
              </div>

              {!showExtras && (extraFees.length > 0 || mem.discount_amount) && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
                  {extraFees.map(f => (
                    <div key={f.id || f.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontFamily: FONT }}>
                      <span style={{ color: T.textSub }}>{f.label} {f.recurring ? "(recurring)" : "(one-time)"}</span>
                      <span style={{ fontWeight: 700, color: T.text }}>${parseFloat(f.amount).toFixed(2)}</span>
                    </div>
                  ))}
                  {mem.discount_amount != null && parseFloat(mem.discount_amount) > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontFamily: FONT }}>
                      <span style={{ color: "#ef4444" }}>
                        {mem.discount_label || "Discount"} {mem.discount_recurring ? "(recurring)" : "(one-time)"}
                      </span>
                      <span style={{ fontWeight: 700, color: "#ef4444" }}>
                        -{mem.discount_type === "pct" ? `${parseFloat(mem.discount_amount)}%` : `$${parseFloat(mem.discount_amount).toFixed(2)}`}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {showExtras && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
                                color: T.textLabel, fontFamily: FONT, marginBottom: 6 }}>Additional Fees</div>
                  {extraFees.map(f => (
                    <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ flex: 1, fontSize: 12, color: T.text, fontFamily: FONT }}>
                        {f.label} — ${parseFloat(f.amount).toFixed(2)} {f.recurring ? "(recurring)" : "(one-time)"}
                      </span>
                      <button onClick={() => removeExtraFee(f.id)} style={{ border: "none", background: "transparent", color: "#ef4444", cursor: "pointer" }}>
                        <FaTrash size={11}/>
                      </button>
                    </div>
                  ))}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 90px auto", gap: 8, marginBottom: 14 }}>
                    <input placeholder="Fee label" value={newFeeLabel} onChange={e => setNewFeeLabel(e.target.value)} style={inp} />
                    <input type="number" placeholder="Amount" value={newFeeAmt} onChange={e => setNewFeeAmt(e.target.value)} style={inp} />
                    <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.textSub, fontFamily: FONT }}>
                      <input type="checkbox" checked={newFeeRecur} onChange={e => setNewFeeRecur(e.target.checked)} /> Recurring
                    </label>
                    <GhostBtn onClick={addExtraFee} T={T} style={{ padding: "6px 10px", fontSize: 11 }}><FaPlus size={9}/></GhostBtn>
                  </div>

                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
                                color: T.textLabel, fontFamily: FONT, marginBottom: 6 }}>Special Discount</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 90px 100px", gap: 8, marginBottom: 14 }}>
                    <input placeholder="Label (e.g. Loyalty)" value={discLabel} onChange={e => setDiscLabel(e.target.value)} style={inp} />
                    <input type="number" placeholder="Amount" value={discAmt} onChange={e => setDiscAmt(e.target.value)} style={inp} />
                    <select value={discType} onChange={e => setDiscType(e.target.value)} style={inp}>
                      <option value="amt">$</option>
                      <option value="pct">%</option>
                    </select>
                    <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.textSub, fontFamily: FONT }}>
                      <input type="checkbox" checked={discRecur} onChange={e => setDiscRecur(e.target.checked)} /> Recurring
                    </label>
                  </div>

                  <Btn T={T} onClick={saveExtras} disabled={savingExtras} style={{ padding: "8px 18px", fontSize: 12 }}>
                    <FaSave size={11}/> {savingExtras ? "Saving…" : "Save"}
                  </Btn>
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "24px 0", color: T.textMuted }}>
            <FaMedal size={36} style={{ opacity: 0.2, marginBottom: 10 }}/>
            <div style={{ fontSize: 14, fontFamily: FONT }}>No active membership</div>
            <div style={{ fontSize: 12, fontFamily: FONT, marginTop: 6 }}>
              Click "Assign Plan" above to set up a membership.
            </div>
          </div>
        )}
      </div>

      {history.length > 1 && (
        <div style={glassCard(T)}>
          <SectionHead title={<><FaHistory size={11} style={{ marginRight: 6 }}/> Membership History</>} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {history.map(h => {
              const isCurrent = h.status === "active";
              let extras = [];
              try { extras = h.extra_fees_json ? JSON.parse(h.extra_fees_json) : []; } catch { extras = []; }
              return (
                <div key={h.membership_id} style={{
                  border: `1px solid ${isCurrent ? "rgba(16,185,129,0.3)" : T.divider}`,
                  background: isCurrent ? "rgba(16,185,129,0.05)" : "transparent",
                  borderRadius: 10, padding: "10px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 800, color: isCurrent ? "#10b981" : T.text, fontFamily: FONT }}>{h.plan_name}</span>
                      <span style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginLeft: 8 }}>
                        {fmtDate(h.start_date)} – {fmtDate(h.end_date)}
                      </span>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 10px", borderRadius: 20,
                                   background: isCurrent ? "rgba(16,185,129,0.15)" : (isDark ? "rgba(255,255,255,0.08)" : "#f1f5f9"),
                                   color: isCurrent ? "#10b981" : T.textMuted, textTransform: "capitalize" }}>
                      {h.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: T.textSub, fontFamily: FONT, marginTop: 4 }}>
                    Paid ${parseFloat(h.amount_paid || 0).toFixed(2)}
                    {extras.map(f => ` · +${f.label} $${parseFloat(f.amount).toFixed(2)}`).join("")}
                    {h.discount_amount != null && parseFloat(h.discount_amount) > 0 &&
                      ` · -${h.discount_label || "Discount"} ${h.discount_type === "pct" ? `${parseFloat(h.discount_amount)}%` : `$${parseFloat(h.discount_amount).toFixed(2)}`}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── 3. Login Tab ──────────────────────────────────────────────────────────────

function LoginTab({ client, T, isDark }) {
  const [info,    setInfo]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPwd,  setNewPwd]  = useState("Welcome2sixers");
  const [confirm, setConfirm] = useState("Welcome2sixers");
  const [working, setWorking] = useState(false);
  const [msg,     setMsg]     = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  // Separate change-password form
  const [chgPwd,  setChgPwd]  = useState("");
  const [chgConf, setChgConf] = useState("");
  const [chgWorking, setChgWorking] = useState(false);
  const [chgMsg,  setChgMsg]  = useState(null);
  const [showChg, setShowChg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}/login`);
      setInfo(await r.json());
    } catch { setInfo(null); }
    setLoading(false);
  }, [client.client_id]);

  useEffect(() => { load(); }, [load]);

  const inp = inputStyle(isDark);

  const handleSetup = async () => {
    if (newPwd.length < 6) { setMsg({ ok: false, text: "Password must be at least 6 characters." }); return; }
    if (newPwd !== confirm) { setMsg({ ok: false, text: "Passwords do not match." }); return; }
    setWorking(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}/setup-login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: newPwd, created_by: "admin" }),
      });
      const d = await r.json();
      if (d.success) {
        setMsg({ ok: true, text: `Login ${d.action === "created" ? "created" : "reset"} for ${d.email}` });
        setNewPwd("Welcome2sixers"); setConfirm("Welcome2sixers");
        load();
      } else { setMsg({ ok: false, text: d.detail || "Failed." }); }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    setWorking(false);
    setTimeout(() => setMsg(null), 5000);
  };

  const handleChangePassword = async () => {
    if (chgPwd.length < 6) { setChgMsg({ ok: false, text: "Min 6 characters." }); return; }
    if (chgPwd !== chgConf) { setChgMsg({ ok: false, text: "Passwords do not match." }); return; }
    setChgWorking(true); setChgMsg(null);
    try {
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}/change-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: chgPwd }),
      });
      const d = await r.json();
      if (d.success) {
        setChgMsg({ ok: true, text: "Password changed successfully." });
        setChgPwd(""); setChgConf(""); setShowChg(false);
        load();
      } else { setChgMsg({ ok: false, text: d.detail || "Failed." }); }
    } catch { setChgMsg({ ok: false, text: "Network error." }); }
    setChgWorking(false);
    setTimeout(() => setChgMsg(null), 5000);
  };

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: T.textMuted }}>Loading…</div>;

  const hasAccount = info?.account != null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Toast ok={msg?.ok} text={msg?.text} />

      {/* Status banner */}
      <div style={glassCard(T)}>
        <SectionHead title={<><FaShieldAlt size={11} style={{ marginRight: 6 }}/> Portal Access</>} />
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px",
                      borderRadius: 10, marginBottom: 16,
                      background: hasAccount ? "rgba(16,185,129,0.07)" : "rgba(245,158,11,0.07)",
                      border: `1px solid ${hasAccount ? "rgba(16,185,129,0.25)" : "rgba(245,158,11,0.25)"}` }}>
          {hasAccount ? <FaCheckCircle size={18} color="#10b981"/> : <FaExclamationCircle size={18} color="#f59e0b"/>}
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, fontFamily: FONT,
                          color: hasAccount ? "#10b981" : "#f59e0b" }}>
              {hasAccount ? "Portal account exists" : "No portal account yet"}
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
              Login email: <strong style={{ color: T.text }}>{info?.email || client.email}</strong>
            </div>
            {hasAccount && (
              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 3 }}>
                Status: <strong style={{ color: info.account.active_status === "active" ? "#10b981" : "#ef4444" }}>
                  {info.account.active_status}
                </strong>
                {info.account.force_password_change
                  ? " · Must change password on next login"
                  : " · Password set by admin"}
              </div>
            )}
          </div>
        </div>

        {/* Create / Reset password */}
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FONT, marginBottom: 10 }}>
          {hasAccount ? "Reset to Default Password" : "Create Portal Login"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
                          color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>
              {hasAccount ? "New Password" : "Password"}
            </div>
            <div style={{ position: "relative" }}>
              <input type={showPwd ? "text" : "password"} value={newPwd}
                onChange={e => setNewPwd(e.target.value)} style={inp} />
              <button onClick={() => setShowPwd(s => !s)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                         background: "none", border: "none", cursor: "pointer", color: T.textMuted, fontSize: 11 }}>
                {showPwd ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
                          color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>Confirm Password</div>
            <input type="password" value={confirm}
              onChange={e => setConfirm(e.target.value)} style={inp} />
          </div>
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginBottom: 14 }}>
          Default: <code style={{ background: isDark ? "rgba(255,255,255,0.1)" : "#f1f5f9",
                                   padding: "1px 6px", borderRadius: 4 }}>Welcome2sixers</code>
          {" "}— customer must change on first login.
        </div>
        <Btn T={T} onClick={handleSetup} disabled={working} style={{ padding: "9px 20px" }}>
          <FaLock size={11}/>
          {working ? "Processing…" : hasAccount ? "Reset to Default" : "Create Login"}
        </Btn>
      </div>

      {/* Change Password button — only shown when account exists */}
      {hasAccount && (
        <div style={glassCard(T)}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showChg ? 14 : 0 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FONT }}>Change Password</div>
              <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
                Set a specific password for this customer.
              </div>
            </div>
            <button onClick={() => setShowChg(s => !s)}
              style={{ padding: "7px 14px", borderRadius: 9, fontSize: 12, fontWeight: 700,
                       cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                       color: "#6366f1", background: "rgba(99,102,241,0.1)",
                       border: "1px solid rgba(99,102,241,0.25)", fontFamily: FONT }}>
              <FaEdit size={11}/> {showChg ? "Cancel" : "Change Password"}
            </button>
          </div>

          {showChg && (
            <>
              <Toast ok={chgMsg?.ok} text={chgMsg?.text} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
                                color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>New Password</div>
                  <input type="password" value={chgPwd}
                    onChange={e => setChgPwd(e.target.value)} placeholder="Min 6 characters" style={inp} />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
                                color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>Confirm Password</div>
                  <input type="password" value={chgConf}
                    onChange={e => setChgConf(e.target.value)} placeholder="Re-enter" style={inp} />
                </div>
              </div>
              <Btn T={T} onClick={handleChangePassword} disabled={chgWorking} style={{ padding: "9px 20px" }}>
                <FaLock size={11}/> {chgWorking ? "Saving…" : "Update Password"}
              </Btn>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── 4. Billing Tab — no KPI header, membership + bookings + card ──────────────

const BILLING_SUB = [
  { key: "all",         label: "All Transactions", icon: <FaHistory size={11}/> },
  { key: "memberships", label: "Paid Fees",         icon: <FaMedal size={11}/> },
  { key: "card",        label: "Payment Profile",   icon: <FaCreditCard size={11}/> },
  { key: "cycle",       label: "Billing Cycle",     icon: <FaRegClock size={11}/> },
];

const CYCLE_LABELS = { monthly: "Monthly", quarterly: "Quarterly", semi_annual: "Semi-Annual", annual: "Annual" };

// ── Lb Refund Modal ───────────────────────────────────────────────────────────
function LbRefundModal({ row, T, isDark, onClose, onDone }) {
  const isHours = row.is_member_rate === 1 && parseFloat(row.total_amount || 0) === 0;
  const isDollar = parseFloat(row.total_amount || 0) > 0;
  const isMixed = row.is_member_rate === 1 && isDollar; // overflow booking

  const totalAmt     = parseFloat(row.total_amount || 0);
  const alreadyRefAmt = parseFloat(row.total_refunded || 0);
  const applicableAmt = Math.max(0, totalAmt - alreadyRefAmt);
  const durMins      = parseInt(row.duration_minutes || 0);
  const durHrs       = parseFloat((durMins / 60).toFixed(2));

  const [refundTypeAmt, setRefundTypeAmt] = useState("full");  // "full" | "partial"
  const [partialAmt,    setPartialAmt]    = useState(applicableAmt.toFixed(2));
  const [refundHrs,     setRefundHrs]     = useState(durHrs.toFixed(2));
  const [partialHours,  setPartialHours]  = useState(false);
  const [newStartTime,  setNewStartTime]  = useState((row.start_time || "").slice(0, 5));

  const tMins = (t) => { if (!t) return 0; const [h, m] = String(t).split(":").map(Number); return h * 60 + (m || 0); };
  const minsToT = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  const remainingMins  = Math.max(0, durMins - Math.round((parseFloat(refundHrs) || 0) * 60));
  const computedEndTime = minsToT(tMins(newStartTime || row.start_time) + remainingMins);
  const fmt12t = (t) => { if (!t) return ""; const [h, m] = String(t).split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; return `${((h % 12) || 12)}:${String(m).padStart(2,"0")} ${ap}`; };
  const [reason,        setReason]        = useState("");
  const [loading,       setLoading]       = useState(false);
  const [result,        setResult]        = useState(null);
  const [error,         setError]         = useState(null);

  const effectiveAmt = refundTypeAmt === "full" ? applicableAmt : Math.min(parseFloat(partialAmt) || 0, applicableAmt);
  const effectiveHrs = partialHours ? Math.min(parseFloat(refundHrs) || 0, durHrs) : durHrs;

  const canSubmit = reason.trim() && (
    (isHours && effectiveHrs > 0) ||
    (isDollar && effectiveAmt > 0)
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true); setError(null);
    try {
      const payload = {
        reason,
        refunded_by: "admin",
        amount: isDollar ? effectiveAmt : 0,
        refund_hours: isHours ? effectiveHrs : undefined,
        partial_hours: isHours ? partialHours : undefined,
        new_start_time: (isHours && partialHours) ? newStartTime : undefined,
      };
      const r = await fetch(`${BASE}/lb/bookings/${row.booking_id}/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "Refund failed."); setLoading(false); return; }
      setResult(d);
    } catch { setError("Network error."); }
    setLoading(false);
  };

  const ov = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9900,
                display: "flex", alignItems: "center", justifyContent: "center" };
  const box = { ...glassCard(T), width: "100%", maxWidth: 500, borderRadius: 18, position: "relative", zIndex: 9901 };
  const labelSt = { fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                    letterSpacing: "0.06em", color: T.textLabel, marginBottom: 5, display: "block" };
  const inputSt = { width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13,
                    background: T.glass, border: T.glassBdr, color: T.text, fontFamily: FONT,
                    boxSizing: "border-box", outline: "none" };

  return (
    <div style={ov} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: FONT }}>
            Refund — {row.booking_ref}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        {result ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#10b981", marginBottom: 8 }}>Refund Processed</div>
            {result.hours_refunded != null && (
              <div style={{ fontSize: 13, color: T.text, marginBottom: 4 }}>
                {result.hours_status === "cancelled"
                  ? `${result.hours_refunded.toFixed(1)} hrs returned to membership pool — booking cancelled`
                  : `${result.hours_refunded.toFixed(1)} hrs returned to pool`}
              </div>
            )}
            {result.new_start_time && result.new_end_time && (
              <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 10,
                            background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#6366f1", textTransform: "uppercase", marginBottom: 4 }}>
                  Updated Booking Window
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>
                  {fmt12t(result.new_start_time)} – {fmt12t(result.new_end_time)}
                </div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                  {result.remaining_hours?.toFixed(1)} hrs remaining — calendar updated
                </div>
              </div>
            )}
            {result.amount_refunded != null && (
              <div style={{ fontSize: 13, color: T.text, marginBottom: 4 }}>
                ${result.amount_refunded.toFixed(2)} refunded via Stripe
              </div>
            )}
            <button onClick={() => { onDone(); onClose(); }}
              style={{ marginTop: 16, padding: "10px 28px", borderRadius: 9, background: "#10b981",
                       color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Summary tiles */}
            <div style={{ display: "grid", gridTemplateColumns: isHours && isDollar ? "1fr 1fr 1fr" : "1fr 1fr", gap: 10, marginBottom: 18 }}>
              {isHours && (
                <div style={{ background: isDark ? "rgba(99,102,241,0.12)" : "#f0f0ff", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", marginBottom: 4 }}>Session</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{durHrs.toFixed(1)} hrs</div>
                </div>
              )}
              {isDollar && (
                <>
                  <div style={{ background: isDark ? "rgba(239,68,68,0.1)" : "#fff5f5", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", marginBottom: 4 }}>Charged</div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>${totalAmt.toFixed(2)}</div>
                  </div>
                  <div style={{ background: isDark ? "rgba(16,185,129,0.1)" : "#f0fdf4", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#10b981", textTransform: "uppercase", marginBottom: 4 }}>Refundable</div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>${applicableAmt.toFixed(2)}</div>
                  </div>
                </>
              )}
            </div>

            {/* Hours refund section */}
            {isHours && (
              <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 10,
                            background: isDark ? "rgba(99,102,241,0.08)" : "#f5f5ff",
                            border: "1px solid rgba(99,102,241,0.18)" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6366f1", marginBottom: 10 }}>
                  Hours Refund — Returns hrs to membership pool
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: partialHours ? 10 : 0 }}>
                  {["full","partial"].map(t => (
                    <button key={t} onClick={() => setPartialHours(t === "partial")}
                      style={{ flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                               background: (t === "partial") === partialHours ? "#6366f1" : T.glass,
                               color: (t === "partial") === partialHours ? "#fff" : T.textLabel,
                               border: (t === "partial") === partialHours ? "none" : T.glassBdr }}>
                      {t === "full" ? `Full (${durHrs.toFixed(1)} hrs)` : "Partial hrs"}
                    </button>
                  ))}
                </div>
                {partialHours && (
                  <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                    <div>
                      <label style={labelSt}>Hours to refund (max {durHrs.toFixed(1)})</label>
                      <input type="number" step="0.5" min="0.5" max={durHrs} value={refundHrs}
                        onChange={e => setRefundHrs(e.target.value)} style={inputSt} />
                    </div>
                    <div>
                      <label style={labelSt}>Booking start time (adjust if needed)</label>
                      <input type="time" value={newStartTime}
                        onChange={e => setNewStartTime(e.target.value)} style={inputSt} />
                    </div>
                    {remainingMins > 0 && (
                      <div style={{ padding: "10px 12px", borderRadius: 8,
                                    background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: "#10b981",
                                      textTransform: "uppercase", marginBottom: 4 }}>Revised Booking Window</div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>
                          {fmt12t(newStartTime)} – {fmt12t(computedEndTime)}
                        </div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                          {(remainingMins / 60).toFixed(1)} hrs remaining in calendar
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Dollar refund section */}
            {isDollar && (
              <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 10,
                            background: isDark ? "rgba(239,68,68,0.07)" : "#fff5f5",
                            border: "1px solid rgba(239,68,68,0.18)" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#ef4444", marginBottom: 10 }}>
                  Stripe Refund
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: refundTypeAmt === "partial" ? 10 : 0 }}>
                  {["full","partial"].map(t => (
                    <button key={t} onClick={() => setRefundTypeAmt(t)}
                      style={{ flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                               background: refundTypeAmt === t ? "#ef4444" : T.glass,
                               color: refundTypeAmt === t ? "#fff" : T.textLabel,
                               border: refundTypeAmt === t ? "none" : T.glassBdr }}>
                      {t === "full" ? `Full ($${applicableAmt.toFixed(2)})` : "Partial $"}
                    </button>
                  ))}
                </div>
                {refundTypeAmt === "partial" && (
                  <div style={{ marginTop: 8 }}>
                    <label style={labelSt}>Amount to refund (max ${applicableAmt.toFixed(2)})</label>
                    <input type="number" step="0.01" min="0.01" max={applicableAmt} value={partialAmt}
                      onChange={e => setPartialAmt(e.target.value)} style={inputSt} />
                  </div>
                )}
              </div>
            )}

            {/* Reason */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelSt}>Reason (required)</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
                placeholder="Enter reason for refund…" style={{ ...inputSt, resize: "vertical" }} />
            </div>

            {error && (
              <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(239,68,68,0.1)",
                            color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{error}</div>
            )}

            <button onClick={handleSubmit} disabled={!canSubmit || loading}
              style={{ width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
                       background: canSubmit ? (isHours && !isDollar ? "#6366f1" : "#ef4444") : T.glass,
                       color: canSubmit ? "#fff" : T.textMuted, fontWeight: 800, fontSize: 14,
                       cursor: canSubmit ? "pointer" : "default", transition: "all 0.2s" }}>
              {loading ? "Processing…"
                : partialHours && remainingMins > 0
                  ? `Refund ${effectiveHrs.toFixed(1)} hrs → Rebook ${fmt12t(newStartTime)}–${fmt12t(computedEndTime)}`
                  : `Confirm Refund${effectiveAmt > 0 ? ` — $${effectiveAmt.toFixed(2)}` : ""}${effectiveHrs > 0 && isHours ? ` + ${effectiveHrs.toFixed(1)} hrs` : ""}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// ── Unified invoice list (bookings + billing charges) — same shape and same
// table design as routers/student_billing.py's /invoices + StudentMaster.js's
// InvoiceTable, so All Transactions / Paid Fees look and behave identically
// whether the profile is a student or a lane client. ─────────────────────────
const REFUNDABLE_STATUSES = ["paid", "confirmed", "completed", "checked_in", "partial_refund"];
const SOURCE_LABEL = { booking: "Booking", charge: "Charge", membership: "Membership" };

function useLaneInvoices(clientId, refreshKey) {
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/lb/clients/${clientId}/invoices`)
      .then(r => r.json())
      .then(d => setInvoices(d.invoices || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);
  useEffect(() => { reload(); }, [reload, refreshKey]);
  return { invoices, loading, reload };
}

function LbChargeRefundModal({ row, clientId, T, isDark, onClose, onDone }) {
  const [refundType, setRefundType] = useState("full");
  const [amount,     setAmount]     = useState("");
  const [reason,     setReason]     = useState("");
  const [working,    setWorking]    = useState(false);
  const [result,     setResult]     = useState(null);

  const totalCharged    = parseFloat(row.amount_due || 0);
  const alreadyRefunded = parseFloat(row.refunded_amount || 0);
  const applicable      = Math.max(0, totalCharged - alreadyRefunded);

  const inp = {
    width: "100%", boxSizing: "border-box", padding: "8px 11px",
    borderRadius: 8, fontSize: 13, outline: "none",
    background: isDark ? "rgba(255,255,255,0.07)" : "#f8fafc",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "#cbd5e1"}`,
    color: T.textPrimary,
  };

  const submit = async () => {
    const partialAmt = parseFloat(amount);
    if (refundType === "partial") {
      if (!amount || isNaN(partialAmt) || partialAmt <= 0 || partialAmt > applicable) {
        setResult({ ok: false, msg: `Enter a valid amount between $0.01 and $${applicable.toFixed(2)}` });
        return;
      }
    }
    if (!reason.trim()) { setResult({ ok: false, msg: "Please enter a reason for the refund." }); return; }
    setWorking(true); setResult(null);
    try {
      const res = await fetch(`${BASE}/lb/clients/${clientId}/charges/${row.source_id}/refund`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refund_type: refundType, amount: refundType === "partial" ? partialAmt : null, reason }),
      });
      const data = await res.json();
      setResult({ ok: data.success, msg: data.message, refundId: data.refund_id, refunded: data.refunded_amount });
      if (data.success) onDone();
    } catch { setResult({ ok: false, msg: "Network error - please try again." }); }
    setWorking(false);
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 };
  const box = { background: isDark ? "#1e293b" : "#fff", borderRadius: 14, padding: 28,
    width: 440, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxHeight: "90vh", overflowY: "auto" };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        {result?.ok ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8, color: T.textPrimary }}>
              Refund ${parseFloat(result.refunded || 0).toFixed(2)} Issued
            </div>
            <div style={{ fontSize: 12, color: getBrandColors().green, background: "rgba(5,150,105,0.08)",
              border: "1px solid rgba(5,150,105,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 18, textAlign: "left" }}>
              {result.msg}
              {result.refundId && <div style={{ marginTop: 4, fontFamily: "monospace", fontSize: 11 }}>Refund ID: {result.refundId}</div>}
            </div>
            <button onClick={onClose} style={{ padding: "9px 24px", borderRadius: 8, border: "none",
              cursor: "pointer", background: getBrandColors().green, color: "#fff", fontWeight: 700, width: "100%" }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary, marginBottom: 4 }}>Issue Refund</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>{row.billing_ref}</div>

            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)", borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 2 }}>CHARGED</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.textPrimary }}>${totalCharged.toFixed(2)}</div>
              </div>
              <div style={{ flex: 1, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)", borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 2 }}>REFUNDED</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: getBrandColors().red }}>${alreadyRefunded.toFixed(2)}</div>
              </div>
              <div style={{ flex: 1, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)", borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 2 }}>AVAILABLE</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: getBrandColors().green }}>${applicable.toFixed(2)}</div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {["full", "partial"].map(t => (
                <button key={t} onClick={() => setRefundType(t)}
                  style={{ flex: 1, padding: "8px 0", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700,
                    background: refundType === t ? getBrandColors().blue : (isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"),
                    color: refundType === t ? "#fff" : T.textSub, border: "none", textTransform: "capitalize" }}>
                  {t}
                </button>
              ))}
            </div>

            {refundType === "partial" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.textLabel, display: "block", marginBottom: 4 }}>Amount ($)</label>
                <input type="number" min="0.01" max={applicable} step="0.01" value={amount}
                  onChange={e => setAmount(e.target.value)} style={inp} placeholder={`up to $${applicable.toFixed(2)}`} />
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: T.textLabel, display: "block", marginBottom: 4 }}>Reason *</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} style={{ ...inp, resize: "vertical" }}
                placeholder="e.g. Customer cancelled, billing error…" />
            </div>

            {result && !result.ok && (
              <div style={{ fontSize: 12, color: getBrandColors().red, background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
                {result.msg}
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onClose} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `1px solid ${T.divider}`,
                background: "transparent", color: T.textSub, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>Cancel</button>
              <button onClick={submit} disabled={working} style={{ flex: 2, padding: "9px 0", borderRadius: 8, border: "none",
                background: getBrandColors().red, color: "#fff", cursor: working ? "not-allowed" : "pointer", fontWeight: 700,
                fontSize: 13, opacity: working ? 0.7 : 1 }}>
                {working ? "Processing…" : `Refund $${(refundType === "full" ? applicable : (parseFloat(amount) || 0)).toFixed(2)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LbChargeCollectModal({ row, clientId, T, isDark, onClose, onDone }) {
  const [mode,      setMode]      = useState("stripe");
  const [manualRef, setManualRef] = useState("");
  const [working,   setWorking]   = useState(false);
  const [result,    setResult]    = useState(null);

  const amountDue = Math.max(0, parseFloat(row.amount_due || 0) - parseFloat(row.refunded_amount || 0));

  const inp = {
    width: "100%", boxSizing: "border-box", padding: "8px 11px",
    borderRadius: 8, fontSize: 13, outline: "none",
    background: isDark ? "rgba(255,255,255,0.07)" : "#f8fafc",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "#cbd5e1"}`,
    color: T.textPrimary,
  };

  const submit = async () => {
    if (mode === "manual" && !manualRef.trim()) {
      setResult({ ok: false, msg: "Enter a reference (cash receipt #, cheque #, etc.)." });
      return;
    }
    setWorking(true); setResult(null);
    try {
      const res = await fetch(`${BASE}/lb/clients/${clientId}/charges/${row.source_id}/collect`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode, manual_ref: manualRef || "Manual", collected_by: "admin" }),
      });
      const data = await res.json();
      setResult({ ok: data.success, msg: data.message });
      if (data.success) onDone();
    } catch { setResult({ ok: false, msg: "Network error - please try again." }); }
    setWorking(false);
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 };
  const box = { background: isDark ? "#1e293b" : "#fff", borderRadius: 14, padding: 28,
    width: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxHeight: "90vh", overflowY: "auto" };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        {result?.ok ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8, color: T.textPrimary }}>Payment Collected</div>
            <div style={{ fontSize: 12, color: getBrandColors().green, background: "rgba(5,150,105,0.08)",
              border: "1px solid rgba(5,150,105,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 18, textAlign: "left" }}>
              {result.msg}
            </div>
            <button onClick={onClose} style={{ padding: "9px 24px", borderRadius: 8, border: "none",
              cursor: "pointer", background: getBrandColors().green, color: "#fff", fontWeight: 700, width: "100%" }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary, marginBottom: 4 }}>Collect Payment</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>{row.billing_ref} — {row.description}</div>

            <div style={{ background: isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)", borderRadius: 8,
              padding: "10px 14px", textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 2 }}>AMOUNT OWING</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.textPrimary }}>${amountDue.toFixed(2)}</div>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[["stripe","Charge Card"],["manual","Manual (Cash/Cheque)"]].map(([m,label]) => (
                <button key={m} onClick={() => setMode(m)}
                  style={{ flex: 1, padding: "8px 4px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700,
                    background: mode === m ? getBrandColors().blue : (isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"),
                    color: mode === m ? "#fff" : T.textSub, border: "none" }}>
                  {label}
                </button>
              ))}
            </div>

            {mode === "manual" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.textLabel, display: "block", marginBottom: 4 }}>Reference *</label>
                <input value={manualRef} onChange={e => setManualRef(e.target.value)} style={inp}
                  placeholder="e.g. Cash receipt #102, cheque #4471" />
              </div>
            )}
            {mode === "stripe" && (
              <div style={{ fontSize: 11.5, color: T.textMuted, marginBottom: 12 }}>
                Charges the client's saved card on file for ${amountDue.toFixed(2)}.
              </div>
            )}

            {result && !result.ok && (
              <div style={{ fontSize: 12, color: getBrandColors().red, background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
                {result.msg}
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onClose} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `1px solid ${T.divider}`,
                background: "transparent", color: T.textSub, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>Cancel</button>
              <button onClick={submit} disabled={working} style={{ flex: 2, padding: "9px 0", borderRadius: 8, border: "none",
                background: getBrandColors().blue, color: "#fff", cursor: working ? "not-allowed" : "pointer", fontWeight: 700,
                fontSize: 13, opacity: working ? 0.7 : 1 }}>
                {working ? "Processing…" : `Collect $${amountDue.toFixed(2)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LaneInvoiceTable({ rows, T, isDark, clientId, bookingsRaw, emptyLabel, onRefund }) {
  const { fmtDate, fmtTime } = useDateTimeFormat();
  const [refundRow, setRefundRow] = useState(null);
  const [collectRow, setCollectRow] = useState(null);
  const invoiceSort = useSort(rows, "transaction_date", "desc");

  if (!rows.length) return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: T.textMuted, fontSize: 14 }}>
      <FaHistory size={28} style={{ marginBottom: 10, opacity: 0.3 }} />
      <div>{emptyLabel || "No records found."}</div>
    </div>
  );

  const sorted = invoiceSort.sorted;
  const thSt = { padding: "10px 12px", textAlign: "left", fontWeight: 800, fontSize: 10,
    letterSpacing: "0.07em", textTransform: "uppercase", color: T.textPrimary,
    borderBottom: `1.5px solid ${T.divider}`, whiteSpace: "nowrap" };
  const tdSt = (bold) => ({ padding: "9px 12px", borderBottom: `1px solid ${T.divider}`,
    color: bold ? T.textPrimary : T.textSub, fontWeight: bold ? 600 : 400, whiteSpace: "nowrap" });

  const openRefund = (r) => {
    if (r.source_type === "booking") {
      const full = (bookingsRaw || []).find(b => b.booking_id === r.source_id);
      if (full) setRefundRow({ kind: "booking", row: full });
    } else {
      setRefundRow({ kind: "charge", row: r });
    }
  };

  return (
    <>
      {refundRow?.kind === "booking" && (
        <LbRefundModal row={refundRow.row} T={T} isDark={isDark}
          onClose={() => setRefundRow(null)}
          onDone={() => { setRefundRow(null); onRefund && onRefund(); }} />
      )}
      {refundRow?.kind === "charge" && (
        <LbChargeRefundModal row={refundRow.row} clientId={clientId} T={T} isDark={isDark}
          onClose={() => setRefundRow(null)}
          onDone={() => { setRefundRow(null); onRefund && onRefund(); }} />
      )}
      {collectRow && (
        <LbChargeCollectModal row={collectRow} clientId={clientId} T={T} isDark={isDark}
          onClose={() => setCollectRow(null)}
          onDone={() => { setCollectRow(null); onRefund && onRefund(); }} />
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: T.glass }}>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("billing_ref")}>Billing Ref #{invoiceSort.sortArrow("billing_ref")}</th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("transaction_date")}>Transaction Date{invoiceSort.sortArrow("transaction_date")}</th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("transaction_time")}>Time{invoiceSort.sortArrow("transaction_time")}</th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("source_type")}>Type{invoiceSort.sortArrow("source_type")}</th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("description")}>Description{invoiceSort.sortArrow("description")}</th>
              <th style={{ ...thSt, textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("amount_due")}>Amount{invoiceSort.sortArrow("amount_due")}</th>
              <th style={thSt}>Refund</th>
              <th style={{ ...thSt, cursor: "pointer", userSelect: "none" }} onClick={() => invoiceSort.toggleSort("status")}>Status{invoiceSort.sortArrow("status")}</th>
              <th style={thSt}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const amtDue    = parseFloat(r.amount_due || 0);
              const refunded  = parseFloat(r.refunded_amount || 0);
              const available = Math.max(0, amtDue - refunded);
              const canRefund = REFUNDABLE_STATUSES.includes(r.status) && available > 0;
              return (
                <tr key={`${r.source_type}-${r.source_id}-${i}`} style={{ background: i % 2 === 0 ? "transparent" : T.glass }}>
                  <td style={{ ...tdSt(true), fontFamily: "monospace", fontSize: 12 }}>{r.billing_ref || "—"}</td>
                  <td style={tdSt(false)}>{fmtDate(r.transaction_date)}</td>
                  <td style={tdSt(false)}>{r.transaction_time ? fmtTime(r.transaction_time) : "—"}</td>
                  <td style={tdSt(false)}>
                    <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: T.glass, color: T.textSub }}>
                      {SOURCE_LABEL[r.source_type] || r.source_type}
                    </span>
                  </td>
                  <td style={{ ...tdSt(false), maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.description || "—"}
                    {(parseFloat(r.prorate_amount || 0) > 0 || parseFloat(r.admin_fee || 0) > 0) && (
                      <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 2, whiteSpace: "nowrap" }}>
                        {parseFloat(r.prorate_amount || 0) > 0 && <span>Membership: ${parseFloat(r.prorate_amount).toFixed(2)}</span>}
                        {parseFloat(r.prorate_amount || 0) > 0 && parseFloat(r.admin_fee || 0) > 0 && <span> · </span>}
                        {parseFloat(r.admin_fee || 0) > 0 && <span>Admin fee: ${parseFloat(r.admin_fee).toFixed(2)}</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdSt(true), textAlign: "right" }}>${amtDue.toFixed(2)}</td>
                  <td style={{ ...tdSt(false), minWidth: 100 }}>
                    {refunded > 0 ? (
                      <div style={{ fontSize: 11, lineHeight: 1.7 }}>
                        <div style={{ color: getBrandColors().red }}>↩ ${refunded.toFixed(2)}</div>
                        <div style={{ color: available > 0 ? getBrandColors().green : T.textMuted }}>Avail: ${available.toFixed(2)}</div>
                      </div>
                    ) : <span style={{ color: T.textMuted, fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ ...tdSt(false), fontSize: 12, color: T.text }}>
                    {r.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </td>
                  <td style={{ ...tdSt(false), display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {canRefund && (
                      <button onClick={() => openRefund(r)}
                        style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #6366f144",
                          background: "rgba(99,102,241,0.08)", color: "#6366f1", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                        ↩ Refund
                      </button>
                    )}
                    {r.source_type === "charge" && ["pending", "failed"].includes(r.status) && (
                      <button onClick={() => setCollectRow(r)}
                        style={{ padding: "4px 12px", borderRadius: 6, border: `1px solid ${getBrandColors().blue}44`,
                          background: `${getBrandColors().blue}14`, color: getBrandColors().blue, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                        💳 Collect
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function BillingTab({ client, T, isDark, onCardChanged, dataVersion, onDataChanged }) {
  const { fmtDate } = useDateTimeFormat();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [sub,     setSub]     = useState("memberships");
  const [msg,     setMsg]     = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [savingCard, setSavingCard] = useState(false);

  const stripeRef  = useRef(null);
  const cardElRef  = useRef(null);
  const mountedRef = useRef(false);

  const loadBilling = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/lb/clients/${client.client_id}/billing`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [client.client_id]);

  // Re-fetch whenever ANY tab in this profile reports a change (membership
  // plan swap, refund, ad-hoc charge, schedule edit) — not just on mount.
  useEffect(() => { loadBilling(); }, [loadBilling, dataVersion]);

  const { invoices, loading: invoicesLoading } = useLaneInvoices(client.client_id, dataVersion);
  const bumpInvoices = () => { onDataChanged && onDataChanged(); };
  const PAID_STATUSES = ["confirmed", "completed", "checked_in", "paid", "refunded", "partial_refund"];

  const thStyle = { padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 800,
                    textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel,
                    borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}` };
  const tdStyle = (extra = {}) => ({ padding: "10px 12px", fontSize: 13, color: T.text,
                                      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9"}`,
                                      fontFamily: FONT, ...extra });

  const handleDeleteCard = async () => {
    if (!window.confirm("Delete this card? Make sure a replacement card is set up first.")) return;
    setDeleting(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}/card`, { method: "DELETE" });
      const d = await r.json();
      if (d.success) {
        setMsg({ ok: true, text: "Card removed." });
        onCardChanged();
      } else { setMsg({ ok: false, text: d.detail || "Failed." }); }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    setDeleting(false);
  };

  const openCardForm = () => {
    setShowCardForm(true);
    setMsg(null);
    mountedRef.current = false;
  };

  const closeCardForm = () => {
    if (cardElRef.current) { try { cardElRef.current.unmount(); } catch {} cardElRef.current = null; }
    mountedRef.current = false;
    setShowCardForm(false);
    setMsg(null);
  };

  useEffect(() => {
    if (!showCardForm || mountedRef.current) return;
    mountedRef.current = true;
    import("@stripe/stripe-js").then(({ loadStripe }) => loadStripe(STRIPE_PK)).then(stripe => {
      stripeRef.current = stripe;
      const elements = stripe.elements();
      const card = elements.create("card", {
        style: { base: { fontSize: "15px", color: isDark ? "#e2e8f0" : "#1e293b", "::placeholder": { color: "#94a3b8" } } },
      });
      card.mount("#lb-card-element");
      cardElRef.current = card;
    });
  }, [showCardForm, isDark]);

  const handleSaveCard = async () => {
    if (!stripeRef.current || !cardElRef.current) return;
    setSavingCard(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/lb/clients/setup-intent`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: client.client_id }),
      });
      const d = await r.json();
      if (!d.client_secret) { setMsg({ ok: false, text: d.detail || "Failed to create setup intent." }); setSavingCard(false); return; }

      const { setupIntent, error } = await stripeRef.current.confirmCardSetup(d.client_secret, {
        payment_method: { card: cardElRef.current, billing_details: { name: `${client.first_name} ${client.last_name}` } },
      });
      if (error) { setMsg({ ok: false, text: error.message }); setSavingCard(false); return; }

      const saveR = await fetch(`${BASE}/lb/clients/save-payment-method`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: client.client_id, payment_method_id: setupIntent.payment_method }),
      });
      const saveD = await saveR.json();
      if (saveD.ok) {
        setMsg({ ok: true, text: "Card saved successfully." });
        closeCardForm();
        onCardChanged();
      } else { setMsg({ ok: false, text: saveD.detail || "Failed to save card." }); }
    } catch (e) { setMsg({ ok: false, text: "Network error." }); }
    setSavingCard(false);
  };

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: T.textMuted }}>Loading…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Toast ok={msg?.ok} text={msg?.text} />

      {/* Sub-tab bar */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {BILLING_SUB.map(s => {
          const active = sub === s.key;
          return (
            <button key={s.key} onClick={() => setSub(s.key)}
              style={{ padding: "8px 16px", borderRadius: 9, fontSize: 12, fontWeight: 700,
                       cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                       color:      active ? T.tabActiveClr : T.textLabel,
                       background: active ? T.tabActiveBg : T.glass,
                       border:     active ? "1px solid rgba(255,200,0,0.4)" : T.glassBdr,
                       backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                       boxShadow:  active ? "0 3px 10px rgba(255,200,0,0.25)" : T.glassSh }}>
              {s.icon} {s.label}
            </button>
          );
        })}
      </div>

      {/* All Transactions — unified, sortable, same table design as the
          student profile's InvoiceTable (bookings + billing charges) */}
      {sub === "all" && (
        invoicesLoading
          ? <div style={{ padding: 30, textAlign: "center", color: T.textMuted }}>Loading…</div>
          : <LaneInvoiceTable rows={invoices} T={T} isDark={isDark} clientId={client.client_id}
              bookingsRaw={data?.bookings || []} onRefund={bumpInvoices}
              emptyLabel="No transactions recorded for this client." />
      )}

      {/* Paid Fees — same invoice list, filtered to paid/settled statuses */}
      {sub === "memberships" && (
        invoicesLoading
          ? <div style={{ padding: 30, textAlign: "center", color: T.textMuted }}>Loading…</div>
          : <LaneInvoiceTable rows={invoices.filter(i => PAID_STATUSES.includes(i.status))} T={T} isDark={isDark}
              clientId={client.client_id} bookingsRaw={data?.bookings || []} onRefund={bumpInvoices}
              emptyLabel="No paid transactions found." />
      )}

      {/* Billing Cycle */}
      {sub === "cycle" && <BillingCycleSubTab clientId={client.client_id} T={T} isDark={isDark} dataVersion={dataVersion} onDataChanged={onDataChanged} />}

      {/* Payment Profile / Card */}
      {sub === "card" && (
        <div style={glassCard(T)}>
          <SectionHead title={<><FaCreditCard size={11} style={{ marginRight: 6 }}/> Payment Profile</>} />
          {client.stripe_pm_id ? (
            <>
              {/* Card display */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                <div style={{ width: 56, height: 38, background: "linear-gradient(135deg,#6366f1,#818cf8)",
                               borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                               flexShrink: 0 }}>
                  <FaCreditCard size={20} color="#fff"/>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: FONT, letterSpacing: "0.04em" }}>
                    {(client.card_brand || "").toUpperCase()} •••• •••• •••• {client.card_last4}
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 3 }}>
                    Expires {String(client.card_exp_month || "").padStart(2,"0")}/{client.card_exp_year}
                  </div>
                  <div style={{ fontSize: 11, color: "#10b981", fontFamily: FONT, marginTop: 4, fontWeight: 700 }}>
                    <FaCheckCircle size={10} style={{ marginRight: 4 }}/>Verified with Stripe
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ padding: "12px 16px", borderRadius: 10,
                            background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.15)",
                            marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: "#ef4444", fontFamily: FONT, fontWeight: 600 }}>
                  ⚠ Before deleting a card, ensure a replacement card has been set up via the Replace button.
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button onClick={openCardForm}
                  style={{ padding: "9px 18px", borderRadius: 9, border: "1px solid rgba(99,102,241,0.35)",
                           background: "rgba(99,102,241,0.1)", color: "#6366f1", fontSize: 13, fontWeight: 700,
                           cursor: "pointer", display: "flex", alignItems: "center", gap: 7, fontFamily: FONT }}>
                  <FaSync size={11}/> Replace Card
                </button>
                <button onClick={handleDeleteCard} disabled={deleting}
                  style={{ padding: "9px 18px", borderRadius: 9, border: "1px solid rgba(239,68,68,0.35)",
                           background: "rgba(239,68,68,0.08)", color: "#ef4444", fontSize: 13, fontWeight: 700,
                           cursor: "pointer", display: "flex", alignItems: "center", gap: 7, fontFamily: FONT,
                           opacity: deleting ? 0.6 : 1 }}>
                  <FaTrash size={11}/> {deleting ? "Removing…" : "Delete Card"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ textAlign: "center", padding: "20px 0 16px", color: T.textMuted, marginBottom: 14 }}>
                <FaCreditCard size={32} style={{ opacity: 0.3, marginBottom: 8 }}/>
                <div style={{ fontSize: 13, fontFamily: FONT }}>No card on file</div>
              </div>
              <Btn T={T} onClick={openCardForm} style={{ padding: "9px 20px" }}>
                <FaCreditCard size={11}/> Add Card
              </Btn>
            </>
          )}

          {/* Stripe card input form */}
          {showCardForm && (
            <div style={{ marginTop: 20, padding: "18px 20px", borderRadius: 12,
                          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(99,102,241,0.04)",
                          border: "1px solid rgba(99,102,241,0.25)" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.textLabel, fontFamily: FONT, marginBottom: 12 }}>
                Enter card details
              </div>
              <div id="lb-card-element"
                style={{ padding: "12px 14px", borderRadius: 8, border: `1.5px solid ${isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                          background: isDark ? "rgba(255,255,255,0.06)" : "#fff", minHeight: 44 }} />
              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button onClick={handleSaveCard} disabled={savingCard}
                  style={{ padding: "9px 22px", borderRadius: 9, border: "none",
                           background: savingCard ? "#94a3b8" : "linear-gradient(135deg,#6366f1,#818cf8)",
                           color: "#fff", fontSize: 13, fontWeight: 700, cursor: savingCard ? "not-allowed" : "pointer",
                           fontFamily: FONT, display: "flex", alignItems: "center", gap: 7 }}>
                  <FaCreditCard size={11}/> {savingCard ? "Saving…" : "Save Card"}
                </button>
                <button onClick={closeCardForm} disabled={savingCard}
                  style={{ padding: "9px 18px", borderRadius: 9,
                           border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#e2e8f0"}`,
                           background: "transparent", color: T.textMuted, fontSize: 13, fontWeight: 600,
                           cursor: "pointer", fontFamily: FONT }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 5a. Billing Cycle Sub-Tab ──────────────────────────────────────────────────

function BillingCycleSubTab({ clientId, T, isDark, dataVersion, onDataChanged }) {
  const { fmtDate } = useDateTimeFormat();
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [showSchedForm,setShowSchedForm]= useState(false);
  const [editId,       setEditId]       = useState(null);
  const [schedForm,    setSchedForm]    = useState({ frequency: "monthly", amount: "", next_run_date: "", period_from: "", period_to: "" });
  const [editForm,     setEditForm]     = useState({});
  const [msg,          setMsg]          = useState(null);
  const [chargingSchedId, setChargingSchedId] = useState(null);
  const [showAdhocForm, setShowAdhocForm] = useState(false);
  const [adhocForm,    setAdhocForm]    = useState({ amount: "", description: "", charge_method: "card" });
  const [adhocSaving,  setAdhocSaving]  = useState(false);

  const inp = { border: `1.5px solid ${T.inputBdr}`, borderRadius: 7, padding: "7px 10px",
                background: T.inputBg, color: T.inputText, fontSize: 13, outline: "none",
                boxSizing: "border-box", fontFamily: FONT };

  const card = (accent) => ({
    background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    border: accent ? `1px solid ${accent}33` : T.glassBdr,
    borderRadius: 14, padding: "18px 20px", marginBottom: 14,
    boxShadow: T.glassSh,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/lb/clients/${clientId}/billing-cycle`);
      setData(await r.json());
    } catch { setData(null); } finally { setLoading(false); }
  }, [clientId]);

  // Re-fetch on mount AND whenever any other tab in this profile (Membership
  // plan swap, a refund/ad-hoc charge in All Transactions) reports a change.
  useEffect(() => { load(); }, [load, dataVersion]);

  const handleAddSchedule = async () => {
    if (!schedForm.amount || !schedForm.next_run_date) {
      setMsg({ ok: false, text: "Amount and Next Billing Date are required." }); return;
    }
    await fetch(`${BASE}/lb/clients/${clientId}/recurring-schedules`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(schedForm),
    });
    setShowSchedForm(false);
    setSchedForm({ frequency: "monthly", amount: "", next_run_date: "", period_from: "", period_to: "" });
    load();
    onDataChanged && onDataChanged();
  };

  const handleAddAdhocCharge = async () => {
    const amt = parseFloat(adhocForm.amount);
    if (!amt || amt <= 0 || !adhocForm.description.trim()) {
      setMsg({ ok: false, text: "Amount and description are required." }); return;
    }
    setAdhocSaving(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/lb/clients/${clientId}/charges`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amt, description: adhocForm.description.trim(), charge_method: adhocForm.charge_method }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Charge failed.");
      setMsg({ ok: true, text: `${d.billing_ref} recorded (${d.status}).` });
      setShowAdhocForm(false);
      setAdhocForm({ amount: "", description: "", charge_method: "card" });
      load();
      onDataChanged && onDataChanged();
    } catch (e) {
      setMsg({ ok: false, text: e.message || "Charge failed." });
    } finally {
      setAdhocSaving(false);
    }
  };

  const handleUpdateSchedule = async (id) => {
    await fetch(`${BASE}/lb/recurring-schedules/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editForm),
    });
    setEditId(null); load();
    onDataChanged && onDataChanged();
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm("Delete this recurring schedule?")) return;
    await fetch(`${BASE}/lb/recurring-schedules/${id}`, { method: "DELETE" });
    load();
    onDataChanged && onDataChanged();
  };

  const chargeScheduleNow = async (id) => {
    if (!window.confirm("Charge this member's saved card for the current billing cycle right now?")) return;
    setChargingSchedId(id); setMsg(null);
    try {
      const res = await fetch(`${BASE}/billing-schedule/schedules/lane_membership/${id}/charge-now`, { method: "POST" });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Charge failed.");
      setMsg({ ok: d.success > 0, text: d.success > 0 ? "Charge succeeded." : d.skipped > 0 ? "Nothing to charge — this cycle is already paid or not yet due." : "Charge failed — see the transaction history for the reason." });
      load();
      onDataChanged && onDataChanged();
    } catch (e) {
      setMsg({ ok: false, text: e.message || "Charge failed." });
    } finally {
      setChargingSchedId(null);
    }
  };

  const schedules     = data?.schedules || [];
  const schedTableSort = useSort(schedules, "next_run_date");

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: T.textMuted, fontFamily: FONT }}>Loading…</div>;

  const membership    = data?.membership;
  const proration     = data?.proration;
  const charges       = data?.charges || [];
  const hoursCredit   = data?.hours_credit || null;
  const cycleLabel    = CYCLE_LABELS[membership?.billing_frequency] || membership?.billing_frequency || "Monthly";
  const cycleFee      = parseFloat(membership?.cycle_fee || 0);
  const adhocCharges  = charges.filter(c => c.source === "adhoc");
  let membershipExtraFees = [];
  try { membershipExtraFees = membership?.extra_fees_json ? JSON.parse(membership.extra_fees_json) : []; } catch { membershipExtraFees = []; }

  const thS = { padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 800,
                textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel,
                borderBottom: `1.5px solid ${T.divider}`, whiteSpace: "nowrap" };
  const tdS = (ex={}) => ({ padding: "10px 10px", fontSize: 13, color: T.text,
                             borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9"}`,
                             fontFamily: FONT, ...ex });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {msg && <div style={{ padding: "10px 14px", borderRadius: 9, fontSize: 13, fontFamily: FONT,
        background: msg.ok ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)",
        color: msg.ok ? "#10b981" : "#ef4444", border: `1px solid ${msg.ok ? "#10b981" : "#ef4444"}33` }}>
        {msg.text}
      </div>}

      {!membership ? (
        <div style={{ ...card(), textAlign: "center", padding: "36px 24px" }}>
          <FaRegClock size={32} style={{ opacity: 0.25, marginBottom: 12 }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FONT, marginBottom: 6 }}>No active membership</div>
          <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT }}>Assign a membership plan in the Membership tab to enable billing schedules.</div>
        </div>
      ) : (
        <>
          {/* ── Plan summary bar (mirrors StudentMaster) ── */}
          <div style={{ ...card(getBrandColors().blue), padding: "14px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px 24px", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel }}>Plan</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginTop: 2, fontFamily: FONT }}>{membership.plan_name}</div>
              </div>
              <div style={{ width: 1, height: 32, background: T.divider }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel }}>Billing Cycle</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: getBrandColors().blue, marginTop: 2, fontFamily: FONT }}>{cycleLabel}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel }}>Cycle Fee</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: getBrandColors().blue, marginTop: 2, fontFamily: FONT }}>${cycleFee.toFixed(2)}</div>
              </div>
              <div style={{ marginLeft: "auto" }}>
                {proration ? (
                  proration.aligned
                    ? <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(16,185,129,0.12)", color: "#10b981", border: "1px solid rgba(16,185,129,0.3)" }}>Aligned — no proration</span>
                    : <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(245,158,11,0.12)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.3)" }}>
                        Prorated: {proration.prorate_days}/{proration.prorate_days_total} days = ${proration.prorate_amount?.toFixed(2)}
                      </span>
                ) : null}
              </div>
            </div>
          </div>

          {/* ── Hours Credit (monthly included hours) ── */}
          {hoursCredit && (
            <div style={card("#6366f1")}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT, marginBottom: 12 }}>Monthly Hours Credit</div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 14 }}>
                {[
                  { label: "Included / Month", value: `${hoursCredit.hours_per_month} hrs`, color: "#6366f1" },
                  { label: "Used This Month",  value: `${hoursCredit.used_hours} hrs`,      color: hoursCredit.used_hours >= hoursCredit.hours_per_month ? "#ef4444" : "#f59e0b" },
                  { label: "Remaining",        value: `${hoursCredit.remaining_hours} hrs`, color: "#10b981" },
                  { label: "Sessions Used",    value: String(hoursCredit.session_count),    color: T.text },
                ].map(({ label, value, color }) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, fontFamily: FONT, marginBottom: 3 }}>{label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: FONT }}>{value}</div>
                  </div>
                ))}
              </div>
              {/* Progress bar */}
              {(() => {
                const pct = Math.min(100, Math.round((hoursCredit.used_hours / hoursCredit.hours_per_month) * 100));
                const barColor = pct >= 100 ? "#ef4444" : pct >= 75 ? "#f59e0b" : "#10b981";
                return (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}>Usage this month</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: barColor, fontFamily: FONT }}>{pct}%</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 99, background: "rgba(99,102,241,0.12)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 99, transition: "width 0.4s" }} />
                    </div>
                    {pct >= 100 && (
                      <div style={{ marginTop: 6, fontSize: 11, color: "#ef4444", fontFamily: FONT, fontWeight: 700 }}>
                        All included hours used — additional bookings charged at member rate.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── One-Time Charge (initial approval charge only — matches the
                student profile's "One-Time Charge" card; recurring and
                ad-hoc charges live in their own sections below, and the
                full history lives in All Transactions / Paid Fees) ── */}
          {(() => {
            const initialCharges = charges.filter(c => parseFloat(c.prorate_amount || 0) > 0 || parseFloat(c.admin_fee || 0) > 0);
            const hasPaidInitial = initialCharges.some(c => c.status === "paid" || c.status === "refunded" || c.status === "partial_refund");
            return (
              <div style={card(hasPaidInitial ? "#10b981" : null)}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT }}>One-Time Charge</div>
                    <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
                      {hasPaidInitial ? "Initial charge collected — record below." : "No approval charge recorded yet."}
                    </div>
                  </div>
                  {hasPaidInitial && (
                    <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: initialCharges.some(c => c.status === "partial_refund") ? "rgba(168,85,247,0.10)" : "rgba(16,185,129,0.12)",
                      color: initialCharges.some(c => c.status === "partial_refund") ? "#a855f7" : "#10b981",
                      border: `1px solid ${initialCharges.some(c => c.status === "partial_refund") ? "#a855f7" : "#10b981"}44` }}>
                      {initialCharges.some(c => c.status === "refunded") ? "Refunded"
                        : initialCharges.some(c => c.status === "partial_refund") ? "Partial Refund"
                        : "Paid ✓"}
                    </span>
                  )}
                </div>

                {initialCharges.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {initialCharges.map(c => {
                      const clr = c.status === "paid" ? "#10b981" : c.status === "pending" ? "#f59e0b" : "#ef4444";
                      const bg  = c.status === "paid" ? "rgba(16,185,129,0.07)" : c.status === "pending" ? "rgba(245,158,11,0.07)" : "rgba(239,68,68,0.07)";
                      const bdr = c.status === "paid" ? "rgba(16,185,129,0.2)" : c.status === "pending" ? "rgba(245,158,11,0.25)" : "rgba(239,68,68,0.25)";
                      const prorateAmt = parseFloat(c.prorate_amount || 0);
                      const adminAmt   = parseFloat(c.admin_fee || 0);
                      return (
                        <div key={c.charge_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                          gap: 12, background: bg, borderRadius: 9, padding: "9px 14px", border: `1px solid ${bdr}` }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: clr, fontFamily: "monospace" }}>{c.billing_ref}</span>
                              <span style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT }}>{fmtDate(c.charge_date)}</span>
                              <span style={{ fontSize: 11, fontWeight: 700, color: clr, textTransform: "capitalize" }}>{c.status?.replace(/_/g," ")}</span>
                            </div>
                            <div style={{ fontSize: 12, color: T.textSub, fontFamily: FONT, marginTop: 3 }}>{c.notes || "One-time charge"}</div>
                            {(prorateAmt > 0 || adminAmt > 0) && (
                              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
                                {prorateAmt > 0 && <span>Membership: ${prorateAmt.toFixed(2)}</span>}
                                {prorateAmt > 0 && adminAmt > 0 && <span> · </span>}
                                {adminAmt > 0 && <span>Admin fee: ${adminAmt.toFixed(2)}</span>}
                              </div>
                            )}
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 800, color: clr, fontFamily: FONT, whiteSpace: "nowrap" }}>${parseFloat(c.total_amount).toFixed(2)}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "16px 0", color: T.textMuted, fontSize: 13, fontFamily: FONT }}>No charges recorded.</div>
                )}
              </div>
            );
          })()}

          {/* ── Recurring Schedule ── */}
          <div style={card()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT }}>Recurring Schedule</div>
                <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>Charged automatically on its due date — use <strong>Charge Now</strong> below to bill this cycle immediately. See Client Management → Billing Hub for run history.</div>
              </div>
              <PrimaryButton size="sm" onClick={() => setShowSchedForm(v => !v)}
                style={{ fontFamily: FONT }}>
                <FaPlus size={10}/> {showSchedForm ? "Cancel" : "Add Schedule"}
              </PrimaryButton>
            </div>

            {showSchedForm && (
              <div style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.03)",
                            border: `1px solid ${T.divider}`, borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, marginBottom: 4, fontFamily: FONT }}>Frequency</div>
                    <select value={schedForm.frequency} onChange={e => setSchedForm(f => ({ ...f, frequency: e.target.value }))}
                      style={{ ...inp, width: "100%", appearance: "none" }}>
                      {Object.entries(CYCLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, marginBottom: 4, fontFamily: FONT }}>Amount ($)</div>
                    <input type="number" min="0" step="0.01" placeholder={cycleFee.toFixed(2)}
                      value={schedForm.amount} onChange={e => setSchedForm(f => ({ ...f, amount: e.target.value }))}
                      style={{ ...inp, width: "100%" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, marginBottom: 4, fontFamily: FONT }}>Next Billing Date</div>
                    <input type="date" value={schedForm.next_run_date} onChange={e => setSchedForm(f => ({ ...f, next_run_date: e.target.value }))}
                      style={{ ...inp, width: "100%" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, marginBottom: 4, fontFamily: FONT }}>Period From</div>
                    <input type="date" value={schedForm.period_from} onChange={e => setSchedForm(f => ({ ...f, period_from: e.target.value }))}
                      style={{ ...inp, width: "100%" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, marginBottom: 4, fontFamily: FONT }}>Period To</div>
                    <input type="date" value={schedForm.period_to} onChange={e => setSchedForm(f => ({ ...f, period_to: e.target.value }))}
                      style={{ ...inp, width: "100%" }} />
                  </div>
                </div>
                <button onClick={handleAddSchedule}
                  style={{ padding: "7px 18px", borderRadius: 8, background: getBrandColors().green, color: "#fff",
                           border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", gap: 6 }}>
                  <FaCheckCircle size={11}/> Save Schedule
                </button>
              </div>
            )}

            {schedules.length === 0 && !showSchedForm ? (
              <div style={{ textAlign: "center", padding: "20px 0", color: T.textMuted, fontSize: 13, fontFamily: FONT }}>
                <FaRegClock size={22} style={{ opacity: 0.25, marginBottom: 6 }} />
                <div>No recurring schedules yet. Click <strong>Add Schedule</strong> to set one up.</div>
              </div>
            ) : schedules.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>{[["Frequency","frequency"],["Plan","plan_name"],["Amount","amount"],["Next Billing","next_run_date"],
                      ["Period From","period_from"],["Period To","period_to"],["Status","active"]].map(([h, key]) => (
                      <th key={h} onClick={() => schedTableSort.toggleSort(key)}
                        style={{ ...thS, cursor: "pointer", userSelect: "none", color: schedTableSort.sortKey === key ? getBrandColors().blue : thS.color }}>
                        {h}{schedTableSort.sortArrow(key)}
                      </th>
                    ))}
                    <th style={thS}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedTableSort.sorted.map(s => editId === s.schedule_id ? (
                      <tr key={s.schedule_id}>
                        <td style={tdS()}>
                          <select value={editForm.frequency} onChange={e => setEditForm(f => ({ ...f, frequency: e.target.value }))}
                            style={{ ...inp, padding: "4px 8px", fontSize: 12 }}>
                            {Object.entries(CYCLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </td>
                        <td style={tdS()}>{s.plan_name || "—"}</td>
                        <td style={tdS()}>
                          <input type="number" min="0" step="0.01" value={editForm.amount}
                            onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                            style={{ ...inp, width: 80, padding: "4px 8px", fontSize: 12 }} />
                        </td>
                        <td style={tdS()}>
                          <input type="date" value={editForm.next_run_date}
                            onChange={e => setEditForm(f => ({ ...f, next_run_date: e.target.value }))}
                            style={{ ...inp, padding: "4px 6px", fontSize: 12 }} />
                        </td>
                        <td style={tdS()}>
                          <input type="date" value={editForm.period_from}
                            onChange={e => setEditForm(f => ({ ...f, period_from: e.target.value }))}
                            style={{ ...inp, padding: "4px 6px", fontSize: 12 }} />
                        </td>
                        <td style={tdS()}>
                          <input type="date" value={editForm.period_to}
                            onChange={e => setEditForm(f => ({ ...f, period_to: e.target.value }))}
                            style={{ ...inp, padding: "4px 6px", fontSize: 12 }} />
                        </td>
                        <td style={tdS()}></td>
                        <td style={tdS()}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => handleUpdateSchedule(s.schedule_id)}
                              style={{ padding: "4px 10px", borderRadius: 6, background: getBrandColors().green, color: "#fff", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Save</button>
                            <button onClick={() => setEditId(null)}
                              style={{ padding: "4px 8px", borderRadius: 6, background: T.glass, color: T.text, border: `1px solid ${T.divider}`, fontSize: 11, cursor: "pointer" }}>✕</button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={s.schedule_id}>
                        <td style={tdS({ fontWeight: 700 })}>{CYCLE_LABELS[s.frequency] || s.frequency}</td>
                        <td style={tdS()}>{s.plan_name || "—"}</td>
                        <td style={tdS({ fontWeight: 700, color: "#6366f1" })}>
                          {fmtMoney(s.amount)}
                          {(() => {
                            const hasRecurFee  = membershipExtraFees.some(f => f.recurring);
                            // Booleanize explicitly — discount_recurring comes back from
                            // the API as a MySQL TINYINT (0/1), and `0 && x` evaluates to
                            // the number 0 (not false); inside a JSX `{...}` expression
                            // React renders that literal "0" as visible text right after
                            // the amount instead of silently omitting it like it would
                            // for false/null/undefined — that's what produced "$275.000".
                            const hasRecurDisc = Boolean(membership?.discount_recurring) && Number(membership?.discount_amount) > 0;
                            if (!membership || membership.plan_id !== s.plan_id || (!hasRecurFee && !hasRecurDisc)) return null;
                            return (
                              <div style={{ fontSize: 10, fontWeight: 400, color: T.textMuted, marginTop: 2, whiteSpace: "nowrap" }}>
                                {membershipExtraFees.filter(f => f.recurring).map(f => `+${f.label} $${parseFloat(f.amount).toFixed(2)}`).join(", ")}
                                {hasRecurFee && hasRecurDisc && ", "}
                                {hasRecurDisc &&
                                  `-${membership.discount_label || "Discount"} ${membership.discount_type === "pct" ? `${parseFloat(membership.discount_amount)}%` : `$${parseFloat(membership.discount_amount).toFixed(2)}`}`}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={tdS()}>{fmtDate(s.next_run_date)}</td>
                        <td style={tdS()}>{fmtDate(s.period_from)}</td>
                        <td style={tdS()}>{fmtDate(s.period_to)}</td>
                        <td style={tdS()}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                            background: s.active ? "rgba(16,185,129,0.12)" : "rgba(107,114,128,0.12)",
                            color: s.active ? "#10b981" : "#6b7280" }}>
                            {s.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={tdS()}>
                          <div style={{ display: "flex", gap: 6 }}>
                            {s.active === 1 && (
                              <PrimaryButton size="sm" onClick={() => chargeScheduleNow(s.schedule_id)} disabled={chargingSchedId === s.schedule_id}
                                title="Manually charge this member's saved card for the current cycle right now"
                                style={{ padding: "4px 10px", fontSize: 11 }}>
                                <FaDollarSign size={9}/> {chargingSchedId === s.schedule_id ? "…" : "Charge Now"}
                              </PrimaryButton>
                            )}
                            <button onClick={() => { setEditId(s.schedule_id); setEditForm({ ...s }); }}
                              style={{ padding: "4px 8px", borderRadius: 6, background: "rgba(99,102,241,0.1)", color: "#6366f1", border: "1px solid rgba(99,102,241,0.2)", fontSize: 11, cursor: "pointer" }}>
                              <FaEdit size={9}/>
                            </button>
                            <button onClick={() => handleDeleteSchedule(s.schedule_id)}
                              style={{ padding: "4px 8px", borderRadius: 6, background: "rgba(239,68,68,0.08)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)", fontSize: 11, cursor: "pointer" }}>
                              <FaTrash size={9}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Ad-Hoc Charges (mirrors StudentMaster's dedicated section) ── */}
          <div style={card()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT }}>Ad-Hoc Charges</div>
                <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 1 }}>One-off charges outside the regular billing schedule.</div>
              </div>
              <PrimaryButton size="sm" onClick={() => setShowAdhocForm(v => !v)}
                style={{ fontFamily: FONT }}>
                <FaPlus size={10}/> {showAdhocForm ? "Cancel" : "Ad-hoc Charge"}
              </PrimaryButton>
            </div>

            {adhocCharges.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: showAdhocForm ? 12 : 0 }}>
                {adhocCharges.map(c => {
                  const clr = c.status === "paid" ? "#10b981" : c.status === "refunded" || c.status === "partial_refund" ? "#a855f7" : c.status === "pending" ? "#f59e0b" : "#ef4444";
                  const bg  = c.status === "paid" ? "rgba(16,185,129,0.07)" : c.status === "refunded" || c.status === "partial_refund" ? "rgba(168,85,247,0.08)" : c.status === "pending" ? "rgba(245,158,11,0.07)" : "rgba(239,68,68,0.07)";
                  const bdr = c.status === "paid" ? "rgba(16,185,129,0.2)" : c.status === "refunded" || c.status === "partial_refund" ? "rgba(168,85,247,0.2)" : c.status === "pending" ? "rgba(245,158,11,0.25)" : "rgba(239,68,68,0.25)";
                  return (
                    <div key={c.charge_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      background: bg, borderRadius: 9, padding: "9px 14px", border: `1px solid ${bdr}` }}>
                      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: clr, fontFamily: "monospace" }}>{c.billing_ref}</span>
                        <span style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT }}>{fmtDate(c.charge_date)}</span>
                        {c.notes && <span style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, fontStyle: "italic" }}>{c.notes}</span>}
                        <span style={{ padding: "2px 9px", borderRadius: 20, fontSize: 10.5, fontWeight: 700, textTransform: "capitalize", color: clr, background: bg, border: `1px solid ${bdr}` }}>{c.status}</span>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 800, color: clr, fontFamily: FONT }}>${parseFloat(c.total_amount).toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {showAdhocForm && (
              <div style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.03)",
                            border: `1px solid ${T.divider}`, borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 10, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, marginBottom: 4, fontFamily: FONT }}>Amount ($)</div>
                    <input type="number" min="0" step="0.01" placeholder="0.00"
                      value={adhocForm.amount} onChange={e => setAdhocForm(f => ({ ...f, amount: e.target.value }))}
                      style={{ ...inp, width: "100%" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, marginBottom: 4, fontFamily: FONT }}>Description</div>
                    <input type="text" placeholder="e.g. Lane damage fee, makeup session…"
                      value={adhocForm.description} onChange={e => setAdhocForm(f => ({ ...f, description: e.target.value }))}
                      style={{ ...inp, width: "100%" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, marginBottom: 4, fontFamily: FONT }}>Method</div>
                    <select value={adhocForm.charge_method} onChange={e => setAdhocForm(f => ({ ...f, charge_method: e.target.value }))}
                      style={{ ...inp, width: "100%", appearance: "none" }}>
                      <option value="card">Charge saved card now</option>
                      <option value="manual">Manual (cash/check) — just record it</option>
                    </select>
                  </div>
                </div>
                <PrimaryButton size="sm" onClick={handleAddAdhocCharge} disabled={adhocSaving}
                  style={{ padding: "8px 16px", fontFamily: FONT }}>
                  {adhocSaving ? "Saving…" : "Add Charge"}
                </PrimaryButton>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── 5. Waiver Tab — multi-waiver list (mirrors StudentMaster WaiverTab) ─────────

function WaiverTab({ client, T, isDark, onSaved }) {
  const { fmtDate } = useDateTimeFormat();
  const [waivers,   setWaivers]   = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file,      setFile]      = useState(null);
  const [form,      setForm]      = useState({
    template_id: "",
    signed_by:   client.first_name ? `${client.first_name} ${client.last_name}` : "",
    signed_on:   new Date().toISOString().split("T")[0],
  });
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  const inp = inputStyle(isDark);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wr, tr] = await Promise.all([
        fetch(`${BASE}/lb/clients/${client.client_id}/waivers`).then(r => r.json()),
        fetch(`${BASE}/waivers/templates`).then(r => r.json()),
      ]);
      setWaivers(wr.waivers || []);
      const allTmpl = [tr.active, ...(tr.history || [])].filter(Boolean);
      setTemplates(allTmpl);
      if (!form.template_id && tr.active) setForm(f => ({ ...f, template_id: String(tr.active.template_id) }));
    } catch { setWaivers([]); } finally { setLoading(false); }
  }, [client.client_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!file) { setMsg({ ok: false, text: "Please select a signed PDF to upload." }); return; }
    if (!form.signed_by.trim()) { setMsg({ ok: false, text: "Signed By is required." }); return; }
    setUploading(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("signed_by", form.signed_by);
      fd.append("signed_on", form.signed_on || "");
      fd.append("template_id", form.template_id || "");
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}/waivers/upload-pdf`, { method: "POST", body: fd });
      const d = await r.json();
      if (d.success) {
        setMsg({ ok: true, text: "Signed waiver uploaded." });
        setShowForm(false); setFile(null);
        setForm(f => ({ ...f, signed_by: `${client.first_name} ${client.last_name}`, signed_on: new Date().toISOString().split("T")[0] }));
        load();
      } else { setMsg({ ok: false, text: d.message || "Upload failed." }); }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    finally { setUploading(false); }
  };

  const handleDelete = async (wId) => {
    if (!window.confirm("Remove this waiver record?")) return;
    const r = await fetch(`${BASE}/lb/clients/${client.client_id}/waivers/${wId}`, { method: "DELETE" });
    const d = await r.json();
    if (d.success) load(); else setMsg({ ok: false, text: d.message || "Failed." });
  };

  const card = { background: T.glass, backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
                 border: `1px solid ${T.divider}`, borderRadius: 14, padding: "18px 20px", marginBottom: 16 };

  return (
    <div>
      <Toast ok={msg?.ok} text={msg?.text} />

      <div style={card}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      marginBottom: showForm || waivers.length > 0 ? 14 : 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary, fontFamily: FONT }}>Signed Waivers</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 1, fontFamily: FONT }}>
              Consent forms signed by the customer.
            </div>
          </div>
          <PrimaryButton size="sm" onClick={() => { setShowForm(v => !v); setMsg(null); setFile(null); }}
            style={{ padding: "5px 12px", fontFamily: FONT }}>
            <FaUpload size={10} /> {showForm ? "Cancel" : "Upload Signed PDF"}
          </PrimaryButton>
        </div>

        {/* Upload form */}
        {showForm && (
          <div style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.04)",
                        borderRadius: 10, padding: "14px 16px",
                        marginBottom: waivers.length > 0 ? 12 : 0, border: `1px solid ${T.inputBdr}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 140px", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                              letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Waiver Template</div>
                <select value={form.template_id}
                  onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))}
                  style={{ ...inp, appearance: "none" }}>
                  <option value="">— Select template —</option>
                  {templates.map(t => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.waiver_name} ({t.version}){t.is_active ? " ★ Active" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                              letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Signed By *</div>
                <input style={inp} value={form.signed_by}
                  onChange={e => setForm(f => ({ ...f, signed_by: e.target.value }))}
                  placeholder="Customer full name" />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                              letterSpacing: "0.06em", color: T.textLabel, marginBottom: 4 }}>Date Signed</div>
                <input type="date" style={inp} value={form.signed_on}
                  onChange={e => setForm(f => ({ ...f, signed_on: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }}
                onChange={e => setFile(e.target.files?.[0] || null)} />
              <button onClick={() => fileRef.current?.click()}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 7,
                         fontSize: 12, fontWeight: 700, cursor: "pointer", background: T.glass,
                         border: `1.5px solid ${T.inputBdr}`, color: T.textPrimary, fontFamily: FONT }}>
                <FaUpload size={11} /> {file ? file.name : "Choose Signed PDF"}
              </button>
              <button onClick={handleUpload} disabled={uploading}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 7,
                         fontSize: 12, fontWeight: 700, cursor: uploading ? "not-allowed" : "pointer",
                         border: "none", background: uploading ? "#94a3b8" : getBrandColors().green,
                         color: "#fff", fontFamily: FONT }}>
                <FaSave size={11} /> {uploading ? "Uploading…" : "Save Waiver"}
              </button>
            </div>
          </div>
        )}

        {/* Waiver list */}
        {loading ? (
          <div style={{ padding: "16px 0", textAlign: "center", color: T.textMuted, fontSize: 13, fontFamily: FONT }}>Loading…</div>
        ) : waivers.length === 0 && !showForm ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: T.textMuted, fontSize: 13, fontFamily: FONT }}>
            <FaFileSignature size={24} style={{ opacity: 0.25, marginBottom: 8 }} />
            <div>No signed waivers on record. Upload a signed PDF to add one.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {waivers.map(w => {
              const hasPdf    = !!w.signed_pdf_url;
              const hasSig    = !!w.signature_url;
              const isRegSig  = w.source === "registration";
              const isGreen   = hasPdf || hasSig;
              const isSynthetic = typeof w.waiver_id === "string";
              return (
                <div key={w.waiver_id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                  background: isGreen
                    ? (isDark ? "rgba(5,150,105,0.10)" : "#dcfce7")
                    : (isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.03)"),
                  borderRadius: 8, padding: "9px 14px",
                  border: `1px solid ${isGreen ? "rgba(5,150,105,0.25)" : T.divider}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
                    <FaFileSignature size={14} color={isGreen ? "#10b981" : T.textMuted} style={{ flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary,
                                    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontFamily: FONT }}>
                        {w.waiver_name || w.template_name || "Member Waiver"}
                        {(w.version || w.template_version) && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 9,
                            background: isGreen ? (isDark ? "rgba(5,150,105,0.2)" : "#dcfce7") : (isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.08)"),
                            color: isGreen ? "#10b981" : T.textSub,
                            border: `1px solid ${isGreen ? "rgba(5,150,105,0.3)" : T.divider}` }}>
                            {w.version || w.template_version}
                          </span>
                        )}
                        {isRegSig && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 9,
                            background: isDark ? "rgba(59,130,246,0.15)" : "#dbeafe",
                            color: "#3b82f6", border: "1px solid rgba(59,130,246,0.30)" }}>
                            Registration
                          </span>
                        )}
                        {hasPdf && <span style={{ fontSize: 10, fontWeight: 700, color: "#10b981" }}>✓ PDF</span>}
                      </div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, fontFamily: FONT }}>
                        {w.signed_by && <span>Signed by <strong>{w.signed_by}</strong></span>}
                        {w.signed_on && <span style={{ marginLeft: 8 }}>{fmtDate(w.signed_on)}</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {hasPdf && (
                      <>
                        <a href={`${BASE}${w.signed_pdf_url}`} target="_blank" rel="noopener noreferrer"
                          style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px",
                                   borderRadius: 6, fontSize: 11, fontWeight: 700, textDecoration: "none",
                                   background: isDark ? "rgba(5,150,105,0.15)" : "#dcfce7",
                                   color: "#10b981", border: "1px solid rgba(5,150,105,0.25)" }}>
                          <FaEye size={9} /> View PDF
                        </a>
                        <a href={`${BASE}${w.signed_pdf_url}`}
                          download={w.signed_pdf_name || `signed_waiver_${w.waiver_id}.pdf`}
                          style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px",
                                   borderRadius: 6, fontSize: 11, fontWeight: 700, textDecoration: "none",
                                   background: isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.07)",
                                   color: T.textSub, border: `1px solid ${T.divider}` }}>
                          <FaDownload size={9} /> Download
                        </a>
                      </>
                    )}
                    {hasSig && !hasPdf && (
                      <a href={`${BASE}${w.signature_url}`} target="_blank" rel="noopener noreferrer"
                        style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px",
                                 borderRadius: 6, fontSize: 11, fontWeight: 700, textDecoration: "none",
                                 background: isDark ? "rgba(5,150,105,0.15)" : "#dcfce7",
                                 color: "#10b981", border: "1px solid rgba(5,150,105,0.25)" }}>
                        <FaFileSignature size={9} /> View Signature
                      </a>
                    )}
                    {!isSynthetic && (
                      <button onClick={() => handleDelete(w.waiver_id)}
                        style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 9px",
                                 borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
                                 background: "rgba(239,68,68,0.08)", color: "#ef4444",
                                 border: "1px solid rgba(239,68,68,0.2)" }}>
                        <FaTrash size={9} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 6. Notes Tab ──────────────────────────────────────────────────────────────

function NotesTab({ client, T, isDark, currentUserId }) {
  const { fmtDate } = useDateTimeFormat();
  const [notes,    setNotes]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [form,     setForm]     = useState({ note_date: new Date().toISOString().split("T")[0], note_text: "" });
  const [msg,      setMsg]      = useState(null);

  const inp = inputStyle(isDark);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/lb/clients/${client.client_id}/notes`);
      const d = await r.json();
      setNotes(d.notes || []);
    } catch { setNotes([]); } finally { setLoading(false); }
  }, [client.client_id]);

  useEffect(() => { load(); }, [load]);

  const saveNote = async () => {
    if (!form.note_text.trim()) { setMsg({ ok: false, text: "Note text is required." }); return; }
    try {
      let r;
      if (editNote) {
        r = await fetch(`${BASE}/lb/client-notes/${editNote.note_id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, updated_by: currentUserId || "admin" }),
        });
      } else {
        r = await fetch(`${BASE}/lb/clients/${client.client_id}/notes`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, created_by: currentUserId || "admin" }),
        });
      }
      const d = await r.json();
      if (d.success) {
        setMsg({ ok: true, text: editNote ? "Note updated." : "Note added." });
        setShowForm(false); setEditNote(null);
        setForm({ note_date: new Date().toISOString().split("T")[0], note_text: "" });
        load();
      } else { setMsg({ ok: false, text: "Failed." }); }
    } catch { setMsg({ ok: false, text: "Network error." }); }
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await fetch(`${BASE}/lb/client-notes/${id}`, { method: "DELETE" });
    load();
  };

  const startEdit = n => {
    setEditNote(n);
    setForm({ note_date: n.note_date || "", note_text: n.note_text || "" });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false); setEditNote(null);
    setForm({ note_date: new Date().toISOString().split("T")[0], note_text: "" });
    setMsg(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Toast ok={msg?.ok} text={msg?.text} />
      <div style={glassCard(T)}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT }}>Staff Notes</div>
            <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
              Internal — visible to staff only.
            </div>
          </div>
          <Btn T={T} onClick={() => showForm && !editNote ? cancelForm() : setShowForm(true)}
            style={{ padding: "7px 14px", fontSize: 12 }}>
            {showForm && !editNote ? <><FaTimes size={10}/> Cancel</> : <><FaPlus size={10}/> Add Note</>}
          </Btn>
        </div>

        {showForm && (
          <div style={{ background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
                        borderRadius: 10, padding: 16, marginBottom: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 12, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase",
                              letterSpacing: "0.06em", color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>Date</div>
                <input type="date" value={form.note_date}
                  onChange={e => setForm(f => ({ ...f, note_date: e.target.value }))} style={inp} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase",
                              letterSpacing: "0.06em", color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>Note</div>
                <textarea value={form.note_text} rows={3}
                  onChange={e => setForm(f => ({ ...f, note_text: e.target.value }))}
                  placeholder="Enter note…"
                  style={{ ...inp, resize: "vertical", lineHeight: 1.5 }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn T={T} onClick={saveNote} style={{ padding: "8px 18px", fontSize: 12 }}>
                <FaSave size={11}/> {editNote ? "Update" : "Save Note"}
              </Btn>
              <GhostBtn onClick={cancelForm} T={T}>Cancel</GhostBtn>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: T.textMuted }}>Loading…</div>
        ) : notes.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: T.textMuted }}>
            <FaStickyNote size={28} style={{ opacity: 0.3, marginBottom: 8 }}/>
            <div style={{ fontSize: 13, fontFamily: FONT }}>No notes yet.</div>
          </div>
        ) : notes.map(n => (
          <div key={n.note_id}
            style={{ padding: "14px 16px", borderRadius: 10, marginBottom: 10,
                     background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                     border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}` }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginBottom: 5 }}>
                  {fmtDate(n.note_date)} · {n.created_by || "staff"}
                </div>
                <div style={{ fontSize: 13, color: T.text, fontFamily: FONT, lineHeight: 1.6 }}>
                  {n.note_text}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button onClick={() => startEdit(n)}
                  style={{ background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)",
                           borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "#6366f1" }}>
                  <FaEdit size={11}/>
                </button>
                <button onClick={() => deleteNote(n.note_id)}
                  style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)",
                           borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "#ef4444" }}>
                  <FaTrash size={11}/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 7. Bookings Tab ───────────────────────────────────────────────────────────

function BookingsTab({ client, T, isDark }) {
  const { fmtDate } = useDateTimeFormat();
  const bookings = client.recent_bookings || [];
  const completed  = bookings.filter(b => b.status === "completed").length;
  const upcoming   = bookings.filter(b => ["confirmed","checked_in","pending_payment"].includes(b.status)).length;
  const totalSpend = bookings.reduce((s, b) => s + parseFloat(b.total_amount || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {[
          { label: "Total Bookings", value: bookings.length,     color: "#6366f1" },
          { label: "Completed",      value: completed,            color: "#10b981" },
          { label: "Upcoming",       value: upcoming,             color: "#f59e0b" },
          { label: "Total Spend",    value: fmtMoney(totalSpend), color: "#0ea5e9" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: `${color}12`, border: `1px solid ${color}30`,
                                    borderRadius: 12, padding: "14px 18px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color, fontFamily: FONT,
                          textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: FONT }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={glassCard(T)}>
        <SectionHead title="Booking History" />
        {bookings.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: T.textMuted }}>
            <FaCalendarAlt size={28} style={{ opacity: 0.3, marginBottom: 8 }}/>
            <div style={{ fontSize: 13, fontFamily: FONT }}>No bookings yet.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT, fontSize: 13 }}>
              <thead>
                <tr>
                  {["Ref","Date","Lane","Duration","Time","Amount","Refunded","Status"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10,
                                         fontWeight: 800, textTransform: "uppercase",
                                         letterSpacing: "0.05em", color: T.textLabel,
                                         borderBottom: `1px solid ${isDark?"rgba(255,255,255,0.08)":"#e2e8f0"}` }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => {
                  const isHrsBk   = parseInt(b.is_member_rate) === 1 && parseFloat(b.total_amount||0) === 0;
                  const isHrsPtn  = b.split_role === "hours_portion";
                  const isChgPtn  = b.split_role === "charge_portion";
                  const durMins   = parseInt(b.duration_minutes || 0);
                  const durLabel  = durMins >= 60
                    ? `${(durMins/60).toFixed(durMins%60===0?0:1)} hr${durMins!==60?"s":""}`
                    : `${durMins} min`;
                  const hrsRef    = parseFloat(b.hours_refunded || 0);
                  const dolRef    = parseFloat(b.total_refunded || 0);
                  return (
                    <tr key={b.booking_id}
                      style={{ borderBottom: `1px solid ${isDark?"rgba(255,255,255,0.05)":"#f1f5f9"}`,
                               background: isHrsPtn ? "rgba(99,102,241,0.03)" : isChgPtn ? "rgba(245,158,11,0.03)" : "transparent" }}>
                      <td style={{ padding: "10px 10px", color: "#6366f1", fontWeight: 700, whiteSpace: "nowrap" }}>
                        {b.split_group_id && (
                          <span style={{ fontSize: 9, fontWeight: 800, marginRight: 4,
                                         background: isHrsPtn ? "rgba(99,102,241,0.15)" : "rgba(245,158,11,0.15)",
                                         color: isHrsPtn ? "#6366f1" : "#f59e0b",
                                         borderRadius: 3, padding: "1px 4px" }}>
                            {isHrsPtn ? "⚡Hrs" : "⚡$"}
                          </span>
                        )}
                        {b.booking_ref}
                      </td>
                      <td style={{ padding: "10px 10px", color: T.text }}>{fmtDate(b.booking_date)}</td>
                      <td style={{ padding: "10px 10px", color: T.text }}>{b.lane_name || "—"}</td>
                      <td style={{ padding: "10px 10px", color: isHrsBk ? "#6366f1" : T.text, fontWeight: isHrsBk ? 700 : 400 }}>
                        {durLabel}{isHrsBk && <span style={{ fontSize: 9, opacity: 0.7, marginLeft: 3 }}>pool</span>}
                      </td>
                      <td style={{ padding: "10px 10px", color: T.textMuted, whiteSpace: "nowrap" }}>
                        {b.start_time?.slice(0,5)} – {b.end_time?.slice(0,5)}
                      </td>
                      <td style={{ padding: "10px 10px", color: T.text, fontWeight: 700 }}>
                        {isHrsBk ? <span style={{ color: "#6366f1", fontSize: 11 }}>Hrs pool</span> : fmtMoney(b.total_amount)}
                      </td>
                      <td style={{ padding: "10px 10px", whiteSpace: "nowrap" }}>
                        {hrsRef > 0
                          ? <span style={{ color: "#6366f1", fontWeight: 700, fontSize: 11 }}>–{hrsRef.toFixed(1)} hrs</span>
                          : dolRef > 0
                          ? <span style={{ color: "#ef4444", fontWeight: 700, fontSize: 11 }}>–{fmtMoney(dolRef)}</span>
                          : <span style={{ color: T.textMuted }}>—</span>}
                      </td>
                      <td style={{ padding: "10px 10px" }}><Chip status={b.status} map={STATUS_CHIP} T={T}/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 8. Student Profile Tab ────────────────────────────────────────────────────

const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];
const PLAYING_ROLE_OPTIONS = ["Batsman", "Bowler", "All-Rounder", "Wicket-Keeper"];

function StudentProfileTab({ client, isDark, T, onSaved }) {
  const { fmtDate } = useDateTimeFormat();
  const API = process.env.REACT_APP_API_URL || "";

  const [linked,        setLinked]        = useState(null);   // fetched student if already linked
  const [loadingLinked, setLoadingLinked] = useState(true);
  const [levels,        setLevels]        = useState([]);
  const [roles,         setRoles]         = useState([]);
  const [loginAccounts, setLoginAccounts] = useState([]);      // parent_accounts linked to this student
  const [newLogin,      setNewLogin]      = useState(null);    // { email, temp_password } — only right after create/link
  const [progHistory,   setProgHistory]   = useState([]);      // student_program_history rows

  // Create form state – pre-fill from client data
  const blankForm = useCallback(() => ({
    first_name:        client.first_name  || "",
    last_name:         client.last_name   || "",
    date_of_birth:     client.date_of_birth || "",
    gender:            client.gender      || "",
    parent_name:       `${client.first_name || ""} ${client.last_name || ""}`.trim(),
    parent_email:      client.email       || "",
    parent_phone:      client.phone       || "",
    join_date:         new Date().toISOString().slice(0, 10),
    skill_level_id:    "",
    playing_role_id:   "",
    cricclub_player_id:"",
    tournament_ready:  0,
    updated_by:        "admin",
  }), [client]);

  const [form,    setForm]    = useState(blankForm);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);
  const [linking, setLinking] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);

  // Load linked student + skill levels + playing roles
  useEffect(() => {
    (async () => {
      try {
        const [lvls, proles] = await Promise.all([
          fetch(`${API}/students/skill-levels`).then(r => r.json()),
          fetch(`${API}/students/playing-roles`).then(r => r.json()),
        ]);
        setLevels(Array.isArray(lvls) ? lvls : []);
        setRoles(Array.isArray(proles) ? proles : []);
      } catch { /* ignore */ }

      if (client.student_id) {
        try {
          // GET /students/{id} wraps the row as { student: {...} }, not flat —
          // reading it flat left every field undefined (shown as "—") except
          // Skill Level/Playing Role, which coincidentally "matched" the first
          // lookup-array entry since both sides of the .find() comparison were
          // undefined.
          const s = await fetch(`${API}/students/${client.student_id}`).then(r => r.json());
          setLinked(s.student || null);
        } catch { /* ignore */ }
        try {
          const la = await fetch(`${API}/parent-accounts/student/${client.student_id}`).then(r => r.json());
          setLoginAccounts(Array.isArray(la.accounts) ? la.accounts : []);
        } catch { /* ignore */ }
        try {
          const ph = await fetch(`${API}/student-billing/${client.student_id}/program-history`).then(r => r.json());
          setProgHistory(Array.isArray(ph.history) ? ph.history : []);
        } catch { /* ignore */ }
      }
      setLoadingLinked(false);
    })();
  }, [client.student_id, API]);

  const field = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Search students for manual link
  useEffect(() => {
    if (!linkSearch.trim()) { setLinkResults([]); return; }
    const t = setTimeout(async () => {
      setLinkSearching(true);
      try {
        const r = await fetch(`${API}/students/?search=${encodeURIComponent(linkSearch)}&page=1&limit=10`).then(r => r.json());
        setLinkResults(Array.isArray(r.students) ? r.students : []);
      } catch { setLinkResults([]); }
      setLinkSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [linkSearch, API]);

  const handleLinkExisting = async (studentId) => {
    setLinking(true);
    setMsg(null);
    try {
      const r = await fetch(`${API}/lb/clients/${client.client_id}/link-student`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: studentId }),
      }).then(r => r.json());
      if (r.ok) {
        setLinked(r.student);
        setShowLink(false);
        setMsg({ type: "success", text: "Student linked successfully." });
        if (r.login) setNewLogin(r.login.temp_password ? r.login : null);
        onSaved && onSaved();
      } else {
        setMsg({ type: "error", text: r.detail || "Link failed." });
      }
    } catch (e) {
      setMsg({ type: "error", text: String(e) });
    }
    setLinking(false);
  };

  const handleCreate = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== "") fd.append(k, v); });

      const res = await fetch(`${API}/students/`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        setMsg({ type: "error", text: data.detail || data.message || "Failed to create student." });
        setSaving(false);
        return;
      }

      // create_student returns { success, message, student: {...} } — the new
      // student's fields (including student_id/student_code) are nested under
      // `student`, not at the top level.
      const newStudent   = data.student || {};
      const newStudentId = newStudent.student_id;
      const lr = await fetch(`${API}/lb/clients/${client.client_id}/link-student`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: newStudentId }),
      }).then(r => r.json());

      if (lr.ok) {
        setLinked({ ...newStudent, ...lr.student });
        setMsg({ type: "success", text: `Student ${newStudent.student_code || ""} created and linked.` });
        if (lr.login) setNewLogin(lr.login.temp_password ? lr.login : null);
        onSaved && onSaved();
      } else {
        setMsg({ type: "success", text: `Student created (code: ${newStudent.student_code}). Link failed: ${lr.detail}` });
      }
    } catch (e) {
      setMsg({ type: "error", text: String(e) });
    }
    setSaving(false);
  };

  const input = (label, key, type = "text", opts = {}) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={e => field(key, e.target.value)}
        style={{
          padding: "8px 10px", borderRadius: 6, border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
          background: isDark ? "#1e293b" : "#fff", color: isDark ? "#e2e8f0" : "#1e293b",
          fontSize: 13,
        }}
        {...opts}
      />
    </div>
  );

  const select = (label, key, options, valKey = "id", lblKey = "name") => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</label>
      <select
        value={form[key]}
        onChange={e => field(key, e.target.value)}
        style={{
          padding: "8px 10px", borderRadius: 6, border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
          background: isDark ? "#1e293b" : "#fff", color: isDark ? "#e2e8f0" : "#1e293b", fontSize: 13,
        }}
      >
        <option value="">— Select —</option>
        {options.map(o => (
          typeof o === "string"
            ? <option key={o} value={o}>{o}</option>
            : <option key={o[valKey]} value={o[valKey]}>{o[lblKey]}</option>
        ))}
      </select>
    </div>
  );

  const card = { background: isDark ? "#1e293b" : "#f8fafc", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`, borderRadius: 10, padding: 20 };
  const sectionHead = { fontSize: 11, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 };

  if (loadingLinked) return <div style={{ padding: 24, color: isDark ? "#94a3b8" : "#64748b", fontSize: 13 }}>Loading…</div>;

  // ── Linked view ───────────────────────────────────────────────────────────────
  if (linked) {
    const lv = levels.find(l => l.level_id === linked.skill_level_id);
    const rl = roles.find(r => r.role_id === linked.playing_role_id);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {msg && (
          <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13,
            background: msg.type === "success" ? (isDark ? "#14532d" : "#dcfce7") : (isDark ? "#450a0a" : "#fee2e2"),
            color: msg.type === "success" ? (isDark ? "#86efac" : "#166534") : (isDark ? "#fca5a5" : "#991b1b"),
          }}>{msg.text}</div>
        )}
        <div style={card}>
          <div style={sectionHead}><FaGraduationCap /> Linked i-Track Student</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
            {[
              ["Student Code",   linked.student_code],
              ["First Name",     linked.first_name],
              ["Last Name",      linked.last_name],
              ["Date of Birth",  fmtDate(linked.date_of_birth)],
              ["Gender",         linked.gender],
              ["Skill Level",    lv?.level_name || linked.skill_level_id || "—"],
              ["Playing Role",   rl?.role_name  || linked.playing_role_id || "—"],
              ["Parent Name",    linked.parent_name],
              ["Parent Email",   linked.parent_email],
              ["Parent Phone",   linked.parent_phone],
              ["Join Date",      fmtDate(linked.join_date)],
              ["Tournament Ready", linked.tournament_ready ? "Yes" : "No"],
            ].map(([lbl, val]) => (
              <div key={lbl} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase" }}>{lbl}</span>
                <span style={{ fontSize: 13, color: isDark ? "#e2e8f0" : "#1e293b" }}>{val || "—"}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={card}>
          <div style={sectionHead}><FaKey /> Portal Login</div>
          {newLogin && (
            <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 12,
              background: isDark ? "#1e3a8a33" : "#dbeafe",
              color: isDark ? "#bfdbfe" : "#1e40af" }}>
              {newLogin.action === "created" ? "New portal login created" : "Linked to existing portal login"}: <strong>{newLogin.email}</strong>
              {newLogin.temp_password && <> — default password <strong>{newLogin.temp_password}</strong> (must be changed on first login)</>}
            </div>
          )}
          {loginAccounts.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {loginAccounts.map(a => (
                <div key={a.parent_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 12px", borderRadius: 8, background: isDark ? "#0f172a" : "#fff",
                  border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }}>
                  <span style={{ fontSize: 13, color: isDark ? "#e2e8f0" : "#1e293b" }}>{a.full_name} — {a.email}</span>
                  <span style={{ fontSize: 11, color: a.active_status ? "#10b981" : "#ef4444", fontWeight: 700 }}>
                    {a.active_status ? "Active" : "Inactive"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>
              No portal login found for this student. Use the client's own Login tab to create one, then it will
              be linked here automatically the next time a student is created or linked from this profile.
            </div>
          )}
        </div>

        <div style={card}>
          <div style={sectionHead}><FaHistory /> Program History</div>
          {progHistory.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {progHistory.map(h => {
                const isCurrent = h.status === "active";
                return (
                  <div key={h.history_id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6,
                    border: `1px solid ${isCurrent ? "rgba(16,185,129,0.3)" : (isDark ? "#334155" : "#e2e8f0")}`,
                    background: isCurrent ? "rgba(16,185,129,0.05)" : "transparent",
                    borderRadius: 9, padding: "8px 12px" }}>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 800, color: isCurrent ? "#10b981" : (isDark ? "#e2e8f0" : "#1e293b") }}>
                        {h.program_name || `Program #${h.program_id}`}
                      </span>
                      <span style={{ fontSize: 10, color: isDark ? "#94a3b8" : "#64748b", marginLeft: 8 }}>
                        {fmtDate(h.start_date)} – {h.end_date ? fmtDate(h.end_date) : "present"}
                      </span>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, textTransform: "capitalize",
                      background: isCurrent ? "rgba(16,185,129,0.15)" : "rgba(148,163,184,0.15)",
                      color: isCurrent ? "#10b981" : "#64748b" }}>{h.status}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>
              No program history yet — this student hasn't been enrolled in a coaching program.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Create form ───────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {msg && (
        <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13,
          background: msg.type === "success" ? (isDark ? "#14532d" : "#dcfce7") : (isDark ? "#450a0a" : "#fee2e2"),
          color: msg.type === "success" ? (isDark ? "#86efac" : "#166534") : (isDark ? "#fca5a5" : "#991b1b"),
        }}>{msg.text}</div>
      )}

      {/* Link existing student panel */}
      <div style={card}>
        <div style={sectionHead}><FaLink /> Link Existing Student</div>
        {!showLink ? (
          <button onClick={() => setShowLink(true)} style={{
            padding: "8px 16px", borderRadius: 7, border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
            background: "transparent", color: isDark ? "#94a3b8" : "#475569", fontSize: 13, cursor: "pointer",
          }}>Search & Link an existing i-Track student</button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input
              type="text"
              placeholder="Type name or student code…"
              value={linkSearch}
              onChange={e => setLinkSearch(e.target.value)}
              style={{
                padding: "8px 10px", borderRadius: 6, border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                background: isDark ? "#0f172a" : "#fff", color: isDark ? "#e2e8f0" : "#1e293b", fontSize: 13,
              }}
            />
            {linkSearching && <span style={{ fontSize: 12, color: isDark ? "#64748b" : "#94a3b8" }}>Searching…</span>}
            {linkResults.map(s => (
              <div key={s.student_id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "8px 12px", borderRadius: 7, background: isDark ? "#0f172a" : "#f1f5f9",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
              }}>
                <span style={{ fontSize: 13, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                  {s.first_name} {s.last_name} <span style={{ color: "#64748b" }}>({s.student_code})</span>
                </span>
                <button
                  onClick={() => handleLinkExisting(s.student_id)}
                  disabled={linking}
                  style={{
                    padding: "5px 12px", borderRadius: 6, border: "none",
                    background: "#0ea5e9", color: "#fff", fontSize: 12, cursor: "pointer",
                  }}
                ><FaLink size={10} style={{ marginRight: 4 }} />Link</button>
              </div>
            ))}
            <button onClick={() => setShowLink(false)} style={{ fontSize: 12, color: "#64748b", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>Cancel</button>
          </div>
        )}
      </div>

      {/* Create new student */}
      <div style={card}>
        <div style={sectionHead}><FaUserPlus /> Create New Student</div>

        {/* Personal */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase", marginBottom: 10 }}>Personal Details</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {input("First Name",    "first_name",    "text", { required: true })}
            {input("Last Name",     "last_name",     "text", { required: true })}
            {input("Date of Birth", "date_of_birth", "date", { required: true })}
            {select("Gender",       "gender",        GENDER_OPTIONS)}
            {input("Join Date",     "join_date",     "date")}
          </div>
        </div>

        {/* Parent / Emergency */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase", marginBottom: 10 }}>Parent / Guardian</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {input("Parent Name",  "parent_name",  "text")}
            {input("Parent Email", "parent_email", "email")}
            {input("Parent Phone", "parent_phone", "tel")}
          </div>
        </div>

        {/* Cricket Profile */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase", marginBottom: 10 }}>Cricket Profile</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {select("Skill Level",   "skill_level_id",  levels, "level_id",  "level_name")}
            {select("Playing Role",  "playing_role_id", roles,  "role_id", "role_name")}
            {input("CricClub Player ID", "cricclub_player_id")}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Tournament Ready</label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: isDark ? "#e2e8f0" : "#1e293b", cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.tournament_ready} onChange={e => field("tournament_ready", e.target.checked ? 1 : 0)} />
                Yes
              </label>
            </div>
          </div>
        </div>

        <button
          onClick={handleCreate}
          disabled={saving || !form.first_name || !form.last_name || !form.date_of_birth}
          style={{
            padding: "10px 24px", borderRadius: 8, border: "none",
            background: saving ? "#334155" : "#0ea5e9", color: "#fff",
            fontSize: 14, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <FaUserPlus size={14} />
          {saving ? "Creating…" : "Create & Link Student"}
        </button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function LaneClientProfile({ clientId, currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);

  const [client,    setClient]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  // Single shared "something changed" signal for the whole profile. Every
  // tab fetches its own data independently (keyed only by the stable
  // clientId), so a plan change in Membership, a refund in All Transactions,
  // or an ad-hoc charge in Billing Cycle would otherwise never tell any
  // OTHER tab's already-fetched state to go re-fetch — the user would see
  // stale data until they navigated away and back. Bumping this counter and
  // including it in every data-fetch effect's dependency array (see
  // BillingTab/BillingCycleSubTab/useLaneInvoices below) fixes that in both
  // directions at once, instead of wiring a one-off callback per pair of tabs.
  const [dataVersion, setDataVersion] = useState(0);
  const bump = useCallback(() => setDataVersion(v => v + 1), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/lb/clients/${clientId}`);
      const d = await r.json();
      setClient(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontFamily: FONT }}>Loading…</div>
  );
  if (!client) return (
    <div style={{ padding: 40, textAlign: "center", color: "#ef4444", fontFamily: FONT }}>Client not found.</div>
  );

  const initials = `${client.first_name?.[0] || ""}${client.last_name?.[0] || ""}`.toUpperCase();
  const cStatusMeta = CLIENT_STATUS[client.status] || { label: client.status, tone: "neutral" };

  return (
    <div style={{ padding: "0 0 48px", fontFamily: FONT }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                       background: "linear-gradient(135deg,rgba(99,102,241,0.25),rgba(139,92,246,0.2))",
                       display: "flex", alignItems: "center", justifyContent: "center",
                       fontSize: 18, fontWeight: 800, color: "#6366f1", fontFamily: FONT }}>
          {initials}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: FONT }}>
              {client.first_name} {client.last_name}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "3px 10px",
                           background: "rgba(99,102,241,0.12)", color: "#6366f1",
                           border: "1px solid rgba(99,102,241,0.25)" }}>
              <FaIdCard size={9} style={{ marginRight: 4 }}/> Customer
            </span>
            <StatusBadge label={cStatusMeta.label} tone={cStatusMeta.tone} />
            {client.registration_purpose && (
              <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "3px 10px",
                             background: "rgba(14,165,233,0.1)", color: "#0ea5e9" }}>
                {PURPOSE_LABEL[client.registration_purpose] || client.registration_purpose}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT,
                        display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span><FaEnvelope size={11} style={{ marginRight: 5 }}/>{client.email}</span>
            {client.phone && <span><FaPhoneAlt size={11} style={{ marginRight: 5 }}/>{client.phone}</span>}
            <span style={{ color: T.textLabel, fontSize: 11 }}>
              CLI-{String(client.client_id).padStart(5,"0")}
            </span>
          </div>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {TABS.map(t => {
          const active = activeTab === t.key;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{
                padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                cursor: "pointer", WebkitTapHighlightColor: "transparent",
                display: "flex", alignItems: "center", gap: 7,
                color:      active ? T.tabActiveClr  : T.textLabel,
                background: active ? T.tabActiveBg : T.glass,
                backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                border:     active ? "1px solid rgba(255,200,0,0.4)" : T.glassBdr,
                boxShadow:  active ? "0 4px 14px rgba(255,200,0,0.30)" : T.glassSh,
                transition: "all 0.15s",
              }}>
              {t.icon} {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab content ── */}
      {activeTab === "details"    && <DetailsTab    client={client} isDark={isDark} T={T} onSaved={() => { load(); bump(); }} />}
      {activeTab === "membership" && <MembershipTab client={client} T={T} isDark={isDark} onSaved={() => { load(); bump(); }} />}
      {activeTab === "login"      && <LoginTab      client={client} T={T} isDark={isDark} />}
      {activeTab === "billing"    && <BillingTab    client={client} T={T} isDark={isDark} onCardChanged={() => { load(); bump(); }} dataVersion={dataVersion} onDataChanged={bump} />}
      {activeTab === "waiver"     && <WaiverTab     client={client} T={T} isDark={isDark} onSaved={() => { load(); bump(); }} />}
      {activeTab === "notes"      && <NotesTab      client={client} T={T} isDark={isDark} currentUserId={currentUserId} />}
      {activeTab === "bookings"   && <BookingsTab      client={client} T={T} isDark={isDark} />}
      {activeTab === "student"    && <StudentProfileTab client={client} T={T} isDark={isDark} onSaved={() => { load(); bump(); }} />}
    </div>
  );
}
